import { apiClient } from './client';
import type { Character } from './character-service';

export type AccountStatus = 'pending' | 'approved' | 'rejected' | 'suspended';
export type TeamPhase = 'testing' | 'active';
export type SignupSource = 'google' | 'otp';

export interface AdminTeam {
  id: number;
  name: string;
  credits: number;
  /** Closed-beta gate state. Pending rows surface Approve/Reject in the action menu. */
  account_status: AccountStatus;
  /**
   * Content mode for the team. 'nsfw' grants full LoRA/prompt access; 'safe'
   * restricts the team to SFW LoRAs and prompts. Set at approval/creation and
   * editable any time via setContentMode (POST /admin/teams/:id/content-mode).
   */
  content_mode: 'safe' | 'nsfw';
  /** Inference provider phase. testing -> Volcengine (CN); active -> BytePlus (SG). */
  phase: TeamPhase;
  inference_discount_pct: number | null;
  purchase_discount_pct: number | null;
  /**
   * Billing-exempt flag. When true, the team is NEVER charged credits and NEVER
   * blocked on balance on any generation path — no Stripe call ever fires.
   * Usage + an imputed retail-credit cost are still recorded on inferences.cost.
   * Toggled via toggleTeamBillingExempt (PATCH /admin/teams/:id/billing-exempt).
   * Defaults false server-side. Orthogonal to is_internal (see below).
   */
  billing_exempt: boolean;
  /**
   * Analytics-exclusion flag — hides the team from cross-team admin usage views
   * unless include_internal is set. ORTHOGONAL to billing_exempt: a comped
   * external partner can be billing_exempt without being is_internal, and vice
   * versa. Read-only in the admin UI today (no write path yet — SQL-only).
   */
  is_internal: boolean;
  /**
   * Auto-top-up state. Surfaced (alongside billing_email + credits) to compute
   * the billing-signal footgun warning when marking a team billing-exempt.
   */
  auto_top_up_enabled?: boolean | null;
  /** Billing/payment contact email. A non-null value + auto-top-up reads as "payment relationship" for the footgun warning. */
  billing_email?: string | null;
  /** Owner profile id. Used for the self-moderation guard in /admin/teams. */
  owner_id: string | null;
  /** Owner profile email. Surfaced as a row subtitle so admins can identify the moderated team. */
  owner_email: string | null;
  /** OAuth provider (google) vs email magic link (otp), derived from the owner's auth.users record. */
  signup_source: SignupSource | null;
  member_count: number;
  created_at: string;
  /** Last write to the team row — coarse "profile updated" signal for vetting. */
  updated_at?: string | null;
  // Company vetting profile (read-only in admin; collected by the team on /pending).
  company_name?: string | null;
  company_address?: string | null;
  company_postcode?: string | null;
  /** ISO 3166-1 alpha-2 code. */
  company_country?: string | null;
  /** VAT / EIN / UEI. */
  company_tax_id?: string | null;
  /** Telegram handle (no @). */
  contact_telegram?: string | null;
  /** Company/billing contact email (read-only here; the team sets it on /pending). */
  company_email?: string | null;
}

export interface AdminTeamFilters {
  status?: AccountStatus | '';
  phase?: TeamPhase | '';
}

/**
 * Support view of a team's balance (GET /admin/teams/:id/balance) — the same
 * credit trio the customer's dashboard renders plus the runway inputs
 * (low-balance threshold, billing exemption), so support can reproduce the
 * "N days left" number a customer reports.
 */
export interface AdminTeamBalance {
  credits: number;
  held_credits: number;
  available_credits: number;
  balance_low_threshold: number | null;
  billing_exempt: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  platform_role: 'user' | 'platform_admin' | 'debugger';
  /** Closed-beta gate state derived from the user's owner team — read-only here. */
  account_status: AccountStatus;
  /** OAuth provider (google) vs email magic link (otp). */
  signup_source: SignupSource;
  credits: number;
  created_at: string;
}

export interface AdminUserFilters {
  source?: SignupSource | '';
}

export interface ApproveTeamPayload {
  credits: number;
  customNote?: string;
  /** Initial content mode for the approved team. Defaults to 'nsfw' server-side when omitted. */
  contentMode?: 'safe' | 'nsfw';
  phase?: TeamPhase;
}

export interface CreateTeamPayload {
  email: string;
  name: string;
  credits: number;
  /** Initial content mode for the pre-created team. Defaults to 'nsfw' server-side when omitted. */
  contentMode?: 'safe' | 'nsfw';
  phase?: TeamPhase;
}

export interface CreateTeamResponse {
  team_id: number;
  owner_user_id: string;
  invite_email_sent: boolean;
}

export interface RejectTeamPayload {
  privateReason: string;
}

export interface SuspendTeamPayload {
  privateReason: string;
}

export interface PendingCountResponse {
  statusCode: number;
  type: string;
  data: { count: number };
}

export interface PhaseCountsResponse {
  statusCode: number;
  type: string;
  data: { testing: number; active: number };
}

export type LoraKind = 'standard' | 'text';

export interface CharacterConsistencyOverrides {
  prompt_template_text?: string;
  trigger_prompt?: string;
  negative_prompt?: string;
  denoise_strength?: number;
  cfg_rescale?: number;
  guidance_scale?: number;
  steps?: number;
  default_weight?: number;
}

export interface LoraCharacterSettings {
  consistency?: CharacterConsistencyOverrides;
  /** synthesis-only profile, managed on /admin/character-settings */
  synthesis?: Record<string, unknown>;
}

export interface AdminLora {
  id: string;
  name: string;
  description?: string;
  preview_image_url?: string;
  /**
   * Explicit-mode thumbnail variant (style thumbnails, Task 6). Written only
   * via the LoRA edit dialog's Explicit-mode slot / `updatePublicLora`.
   * `null`/undefined means the style has no explicit variant — members in
   * nsfw content mode fall back to `preview_image_url` (resolved server-side
   * in `LoraService.sanitizeForUser`). Admin-only — this key must NEVER be
   * added to `Lora`/`StyleSummary` (apps/onlygold/src/shared/api/
   * lora-service.ts), the member-facing shapes; PR-1's leak-guard specs
   * (`lora.service.spec.ts:203`/`:228`, `v1.service.spec.ts:4000`/`:4061`)
   * assert it never reaches either customer surface.
   */
  preview_image_url_nsfw?: string | null;
  kind?: LoraKind;
  lumi_model_id?: string | null;
  lumi_version_id?: string | null;
  default_weight?: number | null;
  category?: string;
  is_public: boolean;
  is_active: boolean;
  /** Owning team id for team-bound LoRAs (is_public=false). Null/undefined for public rows. */
  team_id?: number | null;
  /** Owning team, joined via teams!team_id(name), for admin attribution. Null for public. */
  teams?: { name: string } | null;
  /**
   * NSFW classification (explicit `loras.is_nsfw` column). Drives the SFW/NSFW
   * gating: safe-mode teams only see is_nsfw=false LoRAs. Defaults to true
   * (fail-closed) server-side when an admin omits it on create.
   */
  is_nsfw: boolean;
  /** Curated rank powering /lora/featured. Null = not featured */
  featured_rank?: number | null;
  /**
   * SP3 image-action lifecycle. `action_live_at` is set once the action has
   * been taken live (draft when null); `retired_at` is set once the
   * LoRA-as-style is retired (hidden from playground / /v1/loras / the
   * dispatcher pool while an explicit legacy id keeps working). Orthogonal.
   * `listAllLoras` selects `*`, so both columns are already on the payload.
   */
  action_live_at?: string | null;
  retired_at?: string | null;
  /**
   * Style register (style-registers PR-2 Task 1). A non-null value is what
   * makes this row a resolvable PROMPT STYLE — pinnable directly or picked
   * by the style dispatcher (apps/api/src/admin/style-admin.service.ts).
   * `listPublicLoras`/`listAllLoras` both `select('*')` with no admin-facing
   * denylist, so this has always ridden the payload; only the TS type was
   * missing. Admin-only — this field must NEVER be added to `Lora` or
   * `StyleSummary` (the member-facing shapes in lora-service.ts); PR-1's
   * leak-guard specs (`lora.service.spec.ts:194`, `v1.service.spec.ts:4000`/
   * `:4061`) assert it never reaches either customer surface. Read via the
   * Register tab (`/admin/loras/:id`, Task 4); written via
   * `updateLoraRegister` below.
   */
  register_prompt?: string | null;
  created_at: string;
  prompt_template_id?: string;
  prompt_template_text?: string;
  trigger_prompt?: string;
  default_negative_prompt?: string;
  tags?: string[];
  source_lora_id?: string;
  default_steps?: number;
  default_cfg_rescale?: number;
  default_denoise_strength?: number;
  default_guidance_scale?: number;
  is_character_synthesis_lora?: boolean;
  character_settings?: LoraCharacterSettings | null;
}

export interface PromptTemplate {
  id: string;
  name: string;
  template_text: string;
  description?: string;
  created_at: string;
}

export interface DeliverLoraDto {
  lora_id: string;
  team_ids: number[];
}

export interface LoraDelivery {
  id: string;
  team_id: number;
  teams?: { name: string };
}

export interface LumiLoraItem {
  model_id: string;
  version_id: string;
  name: string;
  task_name: string;
}

export interface CreatePublicLoraDto {
  name: string;
  description?: string;
  preview_image_url?: string;
  /** Explicit-mode thumbnail variant (style thumbnails, Task 6). Send `null` to clear it — the style then falls back to `preview_image_url` for nsfw-mode members. Admin-only. */
  preview_image_url_nsfw?: string | null;
  kind?: LoraKind;
  lumi_model_id?: string;
  lumi_version_id?: string;
  default_weight?: number;
  category?: string;
  tags?: string[];
  is_public: boolean;
  /** NSFW classification. Defaults to true (fail-closed) server-side when omitted. */
  is_nsfw?: boolean;
  default_steps?: number;
  default_cfg_rescale?: number;
  default_denoise_strength?: number;
  default_guidance_scale?: number;
  prompt_template_id?: string;
  prompt_template_text?: string;
  trigger_prompt?: string;
  default_negative_prompt?: string;
  character_settings?: LoraCharacterSettings;
}

export interface AdminInvoice {
  id: number;
  user_id: string;
  team_id: number;
  amount_cents: number;
  currency: string;
  credits: number;
  type: 'credit_purchase' | 'auto_top_up' | string;
  status: 'pending' | 'succeeded' | 'failed' | 'expired' | 'needs_review' | 'refunded' | string;
  /** inqud (crypto) or stripe (historical card payments). */
  provider?: string;
  stripe_payment_intent_id?: string | null;
  /** Inqud checkout id, CCO-…. */
  provider_checkout_id?: string | null;
  provider_status?: string | null;
  paid_currency: string | null;
  paid_blockchain: string | null;
  paid_amount: number | null;
  tx_hash?: string | null;
  /** Why Inqud flagged it: UNDERPAID, OVERPAID, WRONG_CURRENCY, OTHER. */
  review_reason?: string | null;
  created_at: string;
  profiles?: { email: string };
  teams?: { name: string };
}

export type PaymentResolution = 'credit' | 'refunded' | 'write_off';

/** A metered-AI setting row (GET /admin/pricing/metered-ai). */
export interface MeteredAiSetting {
  key: string;
  family: 'ai_markup_pct' | 'ai_min_credits' | 'ai_max_output_tokens';
  label: string;
  value: number;
  min: number;
  max: number;
  integer: boolean;
  description: string;
}

export interface PricingConfig {
  key: string;
  value: number;
  description: string;
  updated_at: string;
}

export interface CreditAdjustmentResult {
  teamId: number;
  previousCredits: number;
  newCredits: number;
  adjustment: number;
  reason: string;
}

export interface AdminBundle {
  id: string;
  name: string;
  description?: string;
  preview_image_url?: string;
  prompt_template_id?: string;
  prompt_template_text?: string;
  trigger_prompt?: string;
  default_negative_prompt?: string;
  is_public: boolean;
  is_active: boolean;
  created_at: string;
  default_steps?: number;
  default_cfg_rescale?: number;
  default_denoise_strength?: number;
  default_guidance_scale?: number;
  // Internal-only "Match keywords" used by the dispatcher to score natural-language
  // prompts against bundles. Stripped from customer-facing responses by
  // BundleService.sanitizeBundlesForUser — admin surfaces only.
  tags?: string[];
  lora_bundle_items?: Array<{ lora_id: string; weight: number; loras?: { name: string; lumi_model_id?: string; lumi_version_id?: string } }>;
}

/** One authored clause row (`action_clauses`). `ord` is derived from array position on write. */
export interface ActionClauseRow {
  id?: string;
  kind: 'base' | 'reference' | 'action';
  ord?: number;
  framing: string | null;
  clause_text: string;
}

export interface ClauseSubsetPreview {
  /** Reference framings present in this subset, ord order ([] = base+action only). */
  framings: string[];
  rendered: string;
  warnings: string[];
}

export interface ClauseValidationResponse {
  valid: boolean;
  errors: string[];
  previews: ClauseSubsetPreview[];
}

export interface AdminVideoLora {
  id: string;
  /** Opaque public id (`lora_<ulid>`), NOT NULL since the PR-2 backfill. Task 8's port omitted this — added here (Task 10) for the detail header's copy-id affordance. */
  aurous_id: string;
  name: string;
  description?: string;
  preview_video_url?: string;
  /** Null on Action rows created without a provider model (clip-driven, no synthesis model bound). */
  model_id: string | null;
  is_public: boolean;
  is_active: boolean;
  /**
   * NSFW classification (explicit `video_loras.is_nsfw` column). Safe-mode teams
   * only see is_nsfw=false video LoRAs. Defaults to true (fail-closed) server-side
   * when an admin omits it on create.
   */
  is_nsfw: boolean;
  team_id?: number;
  default_resolution: string;
  default_ratio: string;
  default_duration: number;
  prompt_template_id?: string;
  prompt_template_text?: string;
  trigger_prompt?: string;
  /** Umbrella-dispatcher match keywords. Empty => not auto-selectable. */
  tags?: string[];
  created_at: string;
  /** Set on every PATCH (admin.service.ts updateVideoLora always stamps it) — the list's "Updated" column. */
  updated_at: string;
  /** Actions (clip-driven video generation) are is_template=true; legacy rows predate the Actions model. */
  is_template: boolean;
  /** Whether generations dispatched off this Action request audio by default. */
  generate_audio_default: boolean;
  /**
   * Clip health rollup — populated for Actions (is_template=true); `null` on
   * legacy (non-Action) rows, which have no `template_clips`.
   */
  clip_counts?: { active: number; ready: number; total: number; failed: number } | null;
  /** Team ids this Action is explicitly granted to. Empty on legacy rows and on public Actions. */
  grant_team_ids?: number[];
}

export interface AdminVideoLoraDetail extends AdminVideoLora {
  /** Team id/name pairs resolved from grant_team_ids — powers the grants editor. */
  grant_teams: Array<{ id: number; name: string }>;
  /**
   * Exact count of inferences that used this video LoRA — same query the
   * backend's delete guard uses. Lets the detail page's delete dialog know
   * BEFORE the admin clicks whether delete would 409, instead of only
   * finding out after (F5).
   */
  generation_count: number;
  /** Ordered structured clause rows; [] when the Action still runs off the legacy template string. */
  clauses: ActionClauseRow[];
}

/**
 * One `lora_audit` row (field-level diff, not the `admin_audit_logs`
 * before/after jsonb shape — see `getVideoLoraAudit`). `old_value`/`new_value`/
 * `reason` are nullable: `writeLoraAudit`/`writeVideoLoraEvent` write `null`
 * for an unset side (e.g. the 'created' event's `old_value` is always null).
 */
export interface AdminVideoLoraAuditRow {
  id: string;
  field: string;
  old_value: string | null;
  new_value: string | null;
  reason: string | null;
  /** Raw actor user id — always present; fall back to this when the profile lookup below is null. */
  changed_by: string;
  /** Resolved from `profiles.email`; null when the profile lookup fails. */
  changed_by_email: string | null;
  created_at: string;
}

export interface AdminActionClip {
  id: string;
  description: string;
  featured: boolean;
  is_active: boolean;
  ord: number;
  created_at: string;
  /** Live provider-asset status for this clip's reference upload; null before ingestion has produced any row. */
  clip_status: 'pending' | 'active' | 'failed' | null;
  clip_error: string | null;
  /** True when clip_status is 'pending' but the claim is stuck past the poll budget + 120s (e.g. a deploy interrupted the worker). */
  stale_pending: boolean;
  /** Signed URL, 1h TTL. */
  preview_url: string | null;
}

export interface ProviderAssetsHealth {
  counts: Array<{ scope: string; status: string; count: number }>;
  /** Drives the Actions sidebar red dot: active clips on active Actions whose LIVE state is failed. */
  live_failed_clips: number;
  /** 10 most recent failed provider_assets rows, newest first. */
  recent_failures: Array<{
    id: string;
    scope: string;
    error: string | null;
    updated_at: string;
    character_ref_id: string | null;
    template_clip_id: string | null;
    video_lora_id: string | null;
    action_name: string | null;
    character_id: string | null;
  }>;
}

export interface CreateVideoLoraDto {
  name: string;
  description?: string;
  preview_video_url?: string;
  /** Optional — Actions are clip-driven and don't require a bound provider model. */
  model_id?: string;
  is_public?: boolean;
  team_id?: number;
  /** Grants immediate team visibility on create. team_id is used as a single-grant fallback when this is omitted. */
  grant_team_ids?: number[];
  prompt_template_id?: string;
  prompt_template_text?: string;
  trigger_prompt?: string;
  /** NSFW classification. Defaults to true (fail-closed) server-side when omitted. */
  is_nsfw?: boolean;
  /** Umbrella-dispatcher match keywords. Empty => not auto-selectable. */
  tags?: string[];
  /** Whether generations dispatched off this Action request audio by default. */
  generate_audio_default?: boolean;
}

export interface CreateBundleDto {
  name: string;
  description?: string;
  preview_image_url?: string;
  items: Array<{ lora_id?: string; model_id: string; version_id: string; name: string; weight: number }>;
  prompt_template_id?: string;
  prompt_template_text?: string;
  trigger_prompt?: string;
  default_negative_prompt?: string;
  is_public: boolean;
  is_active?: boolean;
  // Admin-only: see AdminBundle.tags. Customer-facing reads strip this.
  tags?: string[];
}

export interface DeliverBundleDto {
  bundle_id: string;
  team_ids: number[];
}

// ─── Admin character moderation types ───────────────────────────────────────

export interface AdminCharacter extends Character {
  /** present when soft-deleted */
  deleted_at?: string | null;
  deleted_by_user_id?: string | null;
  deleted_reason?: string | null;
  /** moderation review marker — set when reviewed */
  moderation_reviewed_at?: string | null;
  moderation_reviewed_by_user_id?: string | null;
  /**
   * Synthesis provenance. The admin list selects the whole characters row, so
   * these reach the forensic drawer; the dashboard/V1 character shaper does
   * NOT emit them, which is why they live on AdminCharacter and not Character.
   */
  synthesis_lora_id?: string | null;
  synthesis_inference_ids?: number[] | null;
  /**
   * Task 8: credits this character has REALLY cost — unrefunded charges on a
   * team that was actually billed. A billing-exempt team's imputed rows are
   * excluded by the API, so this and the drawer's Net line always agree.
   *
   * `null` means the charge read FAILED, not that the character cost nothing
   * (R34) — the Cost column renders the two differently, and a `0` here would
   * be a number the platform never actually looked up.
   */
  cost_credits: number | null;
  /**
   * What a BILLING-EXEMPT team would have paid — unrefunded charges that never
   * moved credits. Kept out of `cost_credits` (it is not spend) but shown
   * beside it, so a list of `0 cr` rows cannot read as an idle account when
   * the drawer's ledger is full. `null` on the same read failure.
   */
  imputed_credits: number | null;
  /** Whole days a `draft` has gone untouched; `null` for every other status. */
  draft_age_days: number | null;
  // `nudity` is NOT re-declared here: the base `Character` already types it as
  // the closed `'nude' | 'clothed'` set, and widening it to `string` would be
  // a strictly worse contract on the one surface that filters by it.
}

/** One row of the drawer's Billing tab — `GET /admin/characters/:id/charges`. */
export interface AdminCharacterChargeRow {
  kind: string;
  views_charged: string[];
  /** Postgres NUMERIC — PostgREST can hand these back as strings. */
  unit_price_at_charge: number | string;
  total_credits: number | string;
  /** false = imputed on a billing-exempt team; no credits moved. */
  charged: boolean;
  refunded_at: string | null;
  created_at: string;
  actor_user_id: string | null;
}

export interface AdminCharacterChargesResponse {
  items: AdminCharacterChargeRow[];
}

/**
 * One per-view upload attempt. The raw checker verdict (`fit_verdict`) never
 * crosses the wire, so it has no field here by construction.
 */
export interface AdminReferenceAttemptRow {
  view: string;
  status: 'accepted' | 'rejected';
  reason: string | null;
  detected_view: string | null;
  width_px: number | null;
  height_px: number | null;
  created_at: string;
  user_id: string | null;
}

export interface AdminCharacterAttemptsResponse {
  items: AdminReferenceAttemptRow[];
}

/** A row of the cross-team feed — the per-character fields plus its keys. */
export interface AdminReferenceAttemptFeedRow extends AdminReferenceAttemptRow {
  id: string;
  character_id: string;
  team_id: number;
}

export interface AdminReferenceAttemptListParams {
  team_id?: number;
  /** Closed set — the API rejects anything else with 400. */
  reason?: string;
  view?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface AdminReferenceAttemptListResponse {
  items: AdminReferenceAttemptFeedRow[];
  total: number;
  /** `rate` is null when the filter matched no attempts at all. */
  accept_rate: { accepted: number; attempts: number; rate: number | null };
}

export interface AdminCharacterListResponse {
  items: AdminCharacter[];
  page: number;
  limit: number;
  total: number;
}

export interface AdminCharacterListParams {
  page?: number;
  limit?: number;
  team_id?: number;
  status?: string;
  reviewed?: boolean;
  /** Closed set — the API rejects anything else with 400. */
  nudity?: 'nude' | 'clothed';
  /** true → only rows with deleted_at IS NULL; the API rejects any other value than true/false with 400. */
  exclude_deleted?: boolean;
  /** Case-insensitive substring match on the character name, server-side. */
  search?: string;
}

export interface AdminUsageRow {
  id: number;
  prompt: string;
  status: string;
  output_urls?: string[] | null;
  created_at: string;
  team_id: number;
  user_id: string;
  type?: string;
}

export interface AdminUsageListResponse {
  items: AdminUsageRow[];
  page: number;
  limit: number;
  total: number;
}

/** A single LIGHT (tile-level) generation row for the admin Team ▸ Content tab. */
export interface AdminTeamInferenceRow {
  id: number;
  aurous_id: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed' | string;
  /**
   * Fix wave item 7: `(string & {})` (not a bare `| string`) — see the
   * `style_meta.via` comment further down for why this idiom over a plain
   * union. The column also holds 'chat'/'embedding' rows (PR-1 Task 11.5);
   * narrowing this type to just 'image' | 'video' would make an
   * `=== 'image'` allowlist gate LOOK equivalent to a `!== 'video'` denylist
   * elsewhere, when they aren't — that gap is exactly what fabricated a
   * refund on successful chat/embedding rows before it was fixed (see
   * `AdminInferenceDetail.media_type` below and inference-detail.tsx's
   * `isImageGeneration`).
   */
  media_type: 'image' | 'video' | (string & {}) | null;
  inference_type: string | null;
  prompt: string | null;
  output_urls: string[] | null;
  output_video_url: string | null;
  cost: number | null;
  error_message: string | null;
  user_id: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface AdminTeamInferenceListResponse {
  items: AdminTeamInferenceRow[];
  page: number;
  limit: number;
  total: number;
}

export interface ListTeamInferencesParams {
  page?: number;
  limit?: number;
  status?: string;
  type?: 'image' | 'video';
  start_time?: string;
  end_time?: string;
  q?: string;
  lora_id?: string;
  api_key_id?: number;
}

/** Filter-reactive KPI strip aggregate for the Content tab. */
export interface TeamInferenceStats {
  total: number;
  completed: number;
  success_rate: number;
  credits_spent: number;
  last_active_at: string | null;
}

/** Dropdown options for the Content tab filter ribbon (team-scoped). */
export interface TeamContentFilterOptions {
  loras: Array<{ id: string; name: string }>;
  api_keys: Array<{
    id: number;
    name: string;
    key_prefix: string | null;
    is_active: boolean;
  }>;
}

export interface AdminAuditRow {
  id: string;
  /**
   * `null` = system-originated (no human actor and no profiles row to join) —
   * e.g. the reaper's abandoned-draft purge. Render it as "System", never as a
   * blank or a dash, which read as missing data.
   */
  actor_user_id: string | null;
  actor_email?: string | null;
  action: string;
  target_type: string;
  target_id: string;
  before?: unknown;
  after?: unknown;
  reason?: string | null;
  created_at: string;
}

export interface AdminAuditListResponse {
  items: AdminAuditRow[];
  page: number;
  limit: number;
  total: number;
}

export interface AdminAuditListParams {
  actor?: string;
  action?: string;
  target_type?: string;
  target_id?: string;
  since?: string;
  page?: number;
  limit?: number;
}

// ─── Character settings (synthesis-LoRA control) ────────────────────────────

export type SynthesisMode = 'nsfw' | 'sfw';

/**
 * Shape of `loras.character_settings` jsonb (subset that the frontend reads).
 * Mode lives at `synthesis.mode`; absent ⇒ 'nsfw'.
 */
export interface CharacterSettingsBlob {
  synthesis?: {
    mode?: SynthesisMode;
    prompt_template_text?: string;
    trigger_prompt?: string;
    negative_prompt?: string;
    size?: string;
    pose_prompt_deltas?: Record<
      'portrait' | 'upper' | 'lower' | 'back' | 'ass' | 'side',
      string
    >;
  };
  consistency?: Record<string, unknown>;
}

export interface SynthesisLoraInfo {
  id: string;
  name: string;
  kind?: LoraKind;
  is_active: boolean;
  is_character_synthesis_lora: boolean;
  preview_image_url?: string | null;
  lumi_model_id?: string | null;
  lumi_version_id?: string | null;
  default_weight?: number | null;
  character_settings?: CharacterSettingsBlob | null;
}

export interface SynthesisFlipResult {
  ok: boolean;
  previous: string | null;
  current: string;
}

export interface SynthesisModeFlipResult {
  ok: true;
  mode: SynthesisMode;
  lora_id: string;
}

// ─── Admin team synthesis-pause ─────────────────────────────────────────────

export interface AdminTeamWithSynthesis extends AdminTeam {
  synthesis_paused?: boolean | null;
  synthesis_paused_reason?: string | null;
  synthesis_paused_at?: string | null;
}

// ─── Admin inference lookup (support drilldown) ─────────────────────────────

/**
 * Shape of the `inferences.chain_meta` jsonb column — mirrors
 * `ChainMeta` in apps/api/src/inference/chain/orchestrator.ts. `kind`/
 * `subject_source` are what the admin modal's generation-path badge reads;
 * the v2 write paths mint ONLY 'subject_r2v' | 'subject_chain' — the other
 * three ChainKind literals are legacy-persisted-only (see that type's doc
 * comment) and never freshly minted.
 */
export interface AdminChainMeta {
  /**
   * first-frame-actions: 'seed_still_r2v' is written on the ENGAGED seed leg
   * only — the pinned arm writes the kind alone (no `matched`), the auto arm
   * writes it alongside `matched: true`. All THREE degrade branches carry NO
   * `kind` at all: one pinned ("pinned action has no dispatchable clip", which
   * writes `{degraded_from}`) and two auto (`{matched:false, action_id,
   * degraded_from}` and a bare `{matched:false}`).
   *
   * So `kind` cannot separate pinned from auto, and `seedLegAuthority` in
   * inference-detail.tsx does NOT key on it — nor on the presence of
   * `clip_pick_meta.vision`, which is only its null gate (absent ⇒ the whole
   * subsection renders nothing). The discriminator is `matched === undefined`,
   * because `matched` is written by the auto arm and only the auto arm. See
   * that function's doc for the five-write-site table.
   */
  kind?: 'subject_r2v' | 'subject_chain' | 'seed_still_r2v' | 'character_chain' | 'generated_subject' | 'frame_subject' | string;
  stage?: 'subject' | 'animating' | string;
  matched?: boolean;
  action_id?: string;
  degraded_from?: string;
  subject_source?: 'character' | 'inline' | 'generated' | string;
}

/**
 * SP3 image-Action pick trace persisted on `inferences.action_meta`.
 * `mode`: 'bound' (act clause bound to a resolved subject) vs 'generic'
 * (pure-prompt gen, the still is the only reference).
 * `source`: 'detected' (dispatcher LLM) vs 'pinned' (caller supplied action_id).
 * `method`: how the still was chosen from the count-N pool ('llm' | 'random').
 * `pool_size_at_count`: how many approved stills the pool held for the
 * requested subject count at pick time — the number that explains a bad pick.
 * Widened to `string` on the closed unions so a future value renders instead
 * of type-erroring the admin drawer.
 */
export interface AdminActionMeta {
  mode?: 'bound' | 'generic' | string;
  name?: string;
  source?: 'detected' | 'pinned' | string;
  method?: 'llm' | 'random' | string;
  still_id?: string;
  requested_count?: number;
  pool_size_at_count?: number;
}

/**
 * Raw inference row returned by GET /admin/inferences/:id.
 * Includes admin-only fields (effective_prompt, enhanced_prompt, dispatcher
 * trace) that are stripped from customer-facing responses.
 */
export interface AdminInferenceDetail {
  /** Set when the outputs are served from the 30-day media archive: when that copy stops being served. */
  media_expires_at?: string;
  /** Proxy URLs of the archived copy (the same URLs customers get). Present with media_expires_at. */
  archive_urls?: string[];
  id: number | string;
  team_id: number;
  user_id?: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | string;
  inference_type?: 'image' | 'video' | 'character_synthesis' | string | null;
  /**
   * Real column (image|video|chat|embedding); legacy NULL rows predate the
   * column and are treated as image. `(string & {})` (fix wave item 7, not a
   * bare `| string` — same idiom as `style_meta.via` below) keeps 'image' |
   * 'video' visible for autocomplete while staying open to 'chat'/
   * 'embedding' (PR-1 Task 11.5) and anything future. A narrow two-literal
   * union here is a regression vector: it lets a future reader "simplify"
   * `isImageGeneration`'s `=== 'image' || == null` allowlist gate in
   * inference-detail.tsx into `!== 'video'`, silently resuming the
   * fabricated-refund bug that gate was fixed for.
   */
  media_type?: 'image' | 'video' | (string & {}) | null;
  size_preset?: string | null;
  prompt?: string | null;
  enhanced_prompt?: string | null;
  effective_prompt?: string | null;
  output_urls?: string[] | null;
  stored_urls?: string[] | null;
  output_video_url?: string | null;
  stored_video_url?: string | null;
  reference_image_urls?: string[] | null;
  /** Which Lumi provider served this generation (testing→volcengine, active→byteplus). */
  provider?: 'volcengine' | 'byteplus' | string | null;
  provider_task_id?: string | null;
  provider_request_id?: string | null;
  provider_request_payload?: Record<string, unknown> | null;
  provider_response?: Record<string, unknown> | null;
  image_count?: number | null;
  error_message?: string | null;
  /** Admin-only raw upstream provider error (never returned to customers). */
  error_detail?: string | null;
  created_at: string;
  processing_started_at?: string | null;
  completed_at?: string | null;
  duration_ms?: number | null;
  // Dispatcher trace
  dispatcher_used?: boolean | null;
  dispatcher_outcome?: 'matched' | 'no_match' | 'empty_pool' | 'timeout' | 'error' | 'skipped' | null;
  dispatcher_lora_id?: string | null;
  dispatcher_bundle_id?: string | null;
  dispatcher_pool_size?: number | null;
  dispatcher_latency_ms?: number | null;
  dispatcher_cached_tokens?: number | null;
  dispatcher_reasoning?: string | null;
  // Enhancer LLM outcome trace (introduced 2026-05-26). NULL on legacy rows
  // pre-migration; otherwise one of the enum values (scene_* = W6
  // empty-prompt action gens with a platform-synthesized scene).
  enhancer_outcome?: 'no_request' | 'succeeded' | 'identical' | 'refused' | 'transport_error' | 'length_overflow' | 'scene_synthesized' | 'scene_pool' | null;
  // Joined names from the controller's select
  lora_bundles?: { name: string } | null;
  inference_loras?: Array<{ loras: { id: string; name: string } | null }> | null;
  video_loras?: { name: string } | null;
  // r2v (Seedance 2.0) trace. video_lora_id is set for any video_lora-backed
  // generation; template_clip_id / clip_pick_meta are non-null only when
  // video_lora_id points at an Action pool (is_template=true) and the
  // dispatcher picked one of its clips — that's the gate the UI uses.
  video_lora_id?: string | null;
  template_clip_id?: string | null;
  /**
   * Joined via template_clips!template_clip_id(...) — the clip-picker signal.
   * preview_url is attached server-side (signed from the clip's own
   * storage_path, best-effort) so the modal can render the motion clip the
   * generation was anchored to; absent when signing failed.
   */
  template_clips?: {
    id?: string;
    description: string;
    character_count?: number;
    is_active?: boolean;
    preview_url?: string;
  } | null;
  /**
   * v2 (PR-B): ordered forensic record of the subject references actually
   * sent for a subject_r2v row — admin-only (customer sanitizer strips it).
   * preview_url is attached server-side (signed for storage paths).
   *
   * PR-3a (multi-subject): a count>=2 row persists the GROUPED shape instead
   * — one entry per `subjects[]` element (subject_index/label/source), each
   * carrying its own `entries` array in the SAME per-image shape as the flat
   * form. The two shapes are mutually exclusive per row (never mixed) —
   * `Array.isArray(subject_meta[0]?.entries)` is the discriminant
   * (see `inference-detail.tsx`'s `isGroupedSubjectMeta`).
   */
  subject_meta?:
    | Array<{ framing: string; source_url: string; image_n: number; preview_url?: string }>
    | Array<{
        subject_index: number;
        label: string;
        source: 'character' | 'inline';
        entries: Array<{ framing: string; source_url: string; image_n: number; preview_url?: string }>;
      }>
    | null;
  /**
   * SP3 image Actions (composition acts). Non-null exactly when an act
   * resolved for this generation — auto-detected by the action dispatcher or
   * pinned by the caller via `action_id`. Admin-only forensics: on the V1
   * surface the customer sanitizer DELETES both columns and echoes a derived
   * `action: { id, name } | null` instead — `id` is the raw `action_lora_id`,
   * `name` is `action_meta.name`. `source` (and `mode`/`method`/`still_id`/
   * `requested_count`/`pool_size_at_count`) never leave this surface. The
   * dashboard read keeps the raw columns (sanitize-response.ts, `forV1` gate).
   */
  action_lora_id?: string | null;
  action_meta?: AdminActionMeta | null;
  /**
   * Style registers PR-2 (Task 4 — style forensics). Set by
   * `inference.service.ts` (~:1815) ONLY when a style actually applied to
   * this generation — `via` is one of `explicit` (caller pinned lora_id),
   * `dispatched` (auto style dispatcher matched it), or `alias_superseded`
   * (caller pinned a retired style that resolved to its live successor).
   * `aurous_id`/`name` are the provider-safe echo — NEVER `register_prompt`,
   * which is admin IP that lives on the `loras` row alone (see
   * `AdminLora.register_prompt`) and must never ride this response.
   *
   * NOTE: two OTHER resolutions the design spec's `via` vocabulary lists —
   * `act_alias` (the pinned id was actually an Act, so the generation took
   * the Act path instead — see `ActionCard` above) and `retired_plain` (the
   * pinned style is retired with no successor, so the generation rendered
   * plain SD5) — do NOT populate `style_meta`. Verified in
   * `inference.service.ts`: both branches clear `resolved.loraId` and never
   * set `styleApplied`, so a `retired_plain` pin is currently
   * indistinguishable from "no style ever pinned" in this row's forensics —
   * there is no persisted `style_resolution` column, only this nullable
   * `style_meta`. Flagged as a backend forensics gap (out of scope for this
   * UI task); the Style row below renders only the three resolutions that
   * are actually observable today.
   */
  style_meta?: {
    aurous_id: string;
    name: string;
    /**
     * `(string & {})` (not a bare `| string`) — a bare union collapses the
     * literals to `string` and stops autocomplete/review from documenting
     * the known values at all. This keeps `'explicit' | 'dispatched' |
     * 'alias_superseded'` visible while still accepting a future value.
     */
    via: 'explicit' | 'dispatched' | 'alias_superseded' | (string & {});
  } | null;
  /** Resolved style LoRA id — set together with `style_meta` (same object; both null or both non-null). Links to `/admin/loras/:id`. */
  style_lora_id?: string | null;
  /**
   * SD5-knob names the caller sent that were accepted-and-ignored on this
   * generation (Style registers PR-2 §7). Null/absent on Lumi-routed rows,
   * which honour every knob.
   */
  ignored_params?: string[] | null;
  /**
   * SD2 BYO refs (PR-2 Task 7): 'reference' (default) or 'extend'. Passes
   * through the customer sanitizer too (see sanitize-response.ts) — these are
   * first-class columns, not admin-only — but still forensic here alongside
   * video_ref_meta.
   */
  video_task?: 'reference' | 'extend' | null;
  /** Only meaningful when video_task === 'extend'. */
  extend_direction?: 'forward' | 'backward' | null;
  /**
   * Admin-only forensic blob (sanitize-response.ts strips it from every
   * customer-facing read) — the ids the customer's reference video/audio
   * resolved to, plus a content hash. video_preview_url/duration_s and
   * audio_preview_url/audio_duration_s are attached server-side (signed +
   * looked up from ref_uploads, mirrors subject_meta.preview_url) — absent
   * when the underlying ref_uploads row or signing failed.
   */
  video_ref_meta?: {
    video_ref_upload_id?: string;
    audio_ref_upload_id?: string;
    content_hash?: string;
    video_preview_url?: string;
    duration_s?: number | null;
    audio_preview_url?: string;
    audio_duration_s?: number | null;
  } | null;
  /**
   * Admin-only clip-picker trace. Note the CASING SPLIT: this blob's own keys
   * are snake_case (written key-by-key at inference.service.ts:7325-7331), but
   * `vision` and `trim` are TS objects spread verbatim into JSONB, so their
   * INNER keys are camelCase. Reading `action_id`/`original_seconds` returns
   * undefined and silently renders an empty block.
   */
  clip_pick_meta?: {
    picked_clip_id?: string;
    method?: string;
    /**
     * The CLIP PICKER's no-match (no clip in the act's pool fit the prompt).
     * Distinct from `vision.outcome === 'no_match'`, which is the ACT
     * CLASSIFIER's. Both can be true on one row.
     */
    no_match?: boolean;
    reason?: string;
    /**
     * first-frame-actions: the seed-still act classifier's verdict, mirroring
     * the `ActClassifierVerdict` interface in
     * apps/api/src/video-act-classifier/video-act-classifier.service.ts (named,
     * not numbered — the line number this used to cite was never right).
     * Written on the ENGAGED leg and on EVERY degrade branch, by the single
     * `vision:` spread under `seedLeg` in inference.service.ts, so a row with
     * `vision` but no `template_clip_id` is the visible fallthrough to plain
     * image-to-video.
     */
    vision?: {
      /**
       * `(string & {})` (house idiom, see `style_meta.via` above) so a future
       * outcome renders instead of type-erroring the drawer.
       */
      outcome:
        | 'matched'
        | 'no_match'
        | 'uncertain'
        | 'empty_pool'
        | 'error'
        | 'timeout'
        | 'skipped'
        | (string & {});
      /** Set only when outcome === 'matched'. */
      actionId?: string;
      /** Matched pool item's name — what ops should actually read. */
      actionName?: string;
      /** FORENSICS ONLY — measured `true` on 60/60 calls incl. wrong answers. Never a signal. */
      confident?: boolean;
      /** FORENSICS ONLY — unstable across repeated runs of the same still. Never a signal. */
      personCount?: number;
      latencyMs?: number;
      poolSize?: number;
    };
    /**
     * first-frame-actions Task 10 (inference.service.ts:7339-7343): written on
     * EVERY engaged seed row, trimmed or not. `originalSeconds` is null for an
     * UNMEASURED clip — which is exactly why it took the trim path, and is the
     * common row until the duration backfill runs.
     */
    trim?: {
      applied?: boolean;
      originalSeconds?: number | string | null;
      trimmedToSeconds?: number | string | null;
    };
  } | null;
  /** Chain-orchestration telemetry — see AdminChainMeta. Drives the "Generation path" badge. */
  chain_meta?: AdminChainMeta | null;
  /**
   * Founder forensics (2026-07-14): the subject_chain path's hidden
   * intermediate t2i image (generate-then-animate, no Action) — attached
   * server-side from the parent's hidden child row. Present (object or
   * explicit null) only on video rows; absent on image rows.
   */
  generated_subject_image?: { aurous_id: string; url: string } | null;
  // Allow forward-compatibility with new fields the backend may add.
  [key: string]: unknown;
}

// ─── Storage health (cutover monitoring) ────────────────────────────────────

export interface StorageHealthBucketStatus {
  exists: boolean;
  isPublic: boolean;
}

export interface StorageHealthResponse {
  buckets: {
    aurousPrivate: StorageHealthBucketStatus;
    aurousPublic: StorageHealthBucketStatus;
  };
  lifecycle: {
    refsTtlConfigured: 'yes' | 'no' | 'unknown';
  };
  recent: {
    adminAssetUploadErrors1h: number;
    generationProxy410s1h: number | null;
    totalGenerationProxyHits1h: number | null;
  };
  checked_at: string;
}

// ─── LLM Models (Tasks 16/17/18) ─────────────────────────────────────────────
//
// Internal admin surface — NOT V1. Field names mirror the DB columns
// (snake_case) and Supabase returns NUMERIC as string. The backend always
// returns capital-M (`_usd_per_M`) — keep the property names verbatim or the
// pricing math + serializer round-tripping will silently break.

export type LlmModelKind = 'chat' | 'embedding' | 'video' | 'image';

/**
 * Kinds the admin CREATE form can mint. Image rows are seeded by migration
 * (`20260826000100_image_pricing_registry.sql`) and the server's
 * `CreateAdminModelDto` accepts chat/embedding/video only — an image model
 * needs a `pixel_window` capability shape nothing in this form can author.
 * Their PRICING is fully editable (see `ImageRateTable`); the model row
 * itself is not created here.
 */
export type CreatableLlmModelKind = Exclude<LlmModelKind, 'image'>;

export interface LlmModelCapabilities {
  streaming?: boolean;
  tools?: boolean;
  multimodal_input?: boolean;
  reasoning_effort?: boolean;
  structured_output?: boolean;
  supports_logprobs?: boolean;
  supports_seed?: boolean;
  dimensions?: number[];
  max_input_items?: number;
  /** Video only — subset of ['480p','720p','1080p','4k']. */
  resolutions?: string[];
  /** Video only — SD2: { min: 4, max: 15, adaptive: true }. */
  duration?: { min: number; max: number; adaptive: boolean };
  /** Video only — SD2: 24. */
  fps?: number;
  /** Video only — subset of ['t2v','i2v_first','i2v_frames','multimodal_reference']. */
  modes?: string[];
  /** Image only — total-output-pixel window the model can render, inclusive. */
  pixel_window?: { min: number; max: number };
  /** Image only — provider-side render window, narrower than pixel_window on some rows. */
  provider_render_window?: { min: number; max: number };
  /** Image only — per-side bounds for CUSTOM width/height requests (NOT the size presets). */
  side?: { min: number; max: number };
  /** Image only — custom width/height must be a multiple of this. */
  snap?: number;
  /** Image only — max images per request. */
  max_count?: number;
  /** Image only — reference/context image caps. */
  ref_caps?: { reference: number; context: number };
}

/**
 * Video rate-matrix row. Mirrors `apps/api/src/llm/video-pricing.types.ts`
 * (`VideoRateRow`) verbatim — snake_case fields persist as-is in the JSONB
 * `rate_table` column.
 */
export interface VideoRateRow {
  /** Subset of ['480p','720p','1080p','4k']. */
  resolutions: string[];
  video_input: boolean;
  /** > 0 — PROVIDER cost rate (margin tracking). */
  usd_per_M: number;
  /**
   * > 0 — RETAIL billing rate. When present, retail is final and markup does
   * NOT apply (effective billing rate = usd_per_M_retail ?? usd_per_M × (1 + markup_pct/100)).
   */
  usd_per_M_retail?: number;
}

export interface VideoRateTable {
  unit: 'usd_per_M_tokens';
  rows: VideoRateRow[];
}

/**
 * Image rate-matrix row. Mirrors `apps/api/src/llm/image-pricing.types.ts`
 * (`ImageRateRow`) verbatim — snake_case fields persist as-is in the JSONB
 * `rate_table` column.
 */
export interface ImageRateRow {
  /** Inclusive upper pixel bound for this tier; null = the open-ended terminal row. */
  max_output_px: number | null;
  /** > 0 — PROVIDER cost per image (margin tracking only). */
  usd: number;
  /**
   * > 0 — RETAIL price per image. When present it is FINAL and markup does
   * NOT apply (same semantics as video's `usd_per_M_retail`).
   */
  usd_retail?: number;
}

export interface ImageRateTable {
  unit: 'usd_per_image';
  /** Provider charge per input image from the (included+1)-th onward. ABSORBED —
   *  never billed to customers; carried for COGS telemetry only. */
  input_image_usd: number;
  input_images_included: number;
  /** Ascending by `max_output_px`; the LAST row (and only the last) is open-ended. */
  rows: ImageRateRow[];
}

/** The jsonb `rate_table` as stored — one shape per matrix-priced kind. */
export type LlmRateTable = VideoRateTable | ImageRateTable;

/** Narrows a stored rate_table to the video shape. Keyed on `unit`, the
 *  discriminator both server validators already enforce. */
export const isVideoRateTable = (t: LlmRateTable | null | undefined): t is VideoRateTable =>
  t?.unit === 'usd_per_M_tokens';

/** Narrows a stored rate_table to the image shape. */
export const isImageRateTable = (t: LlmRateTable | null | undefined): t is ImageRateTable =>
  t?.unit === 'usd_per_image';

export interface LlmModelPricing {
  id: string;
  model_id: string;
  version: number;
  /** USD per million input tokens (chat). Supabase returns NUMERIC as string. */
  input_usd_per_M: string | null;
  output_usd_per_M: string | null;
  /** Admin-only — internal margin field, never exposed via V1. */
  cache_hit_usd_per_M: string | null;
  input_text_usd_per_M: string | null;
  input_visual_usd_per_M: string | null;
  input_video_usd_per_M: string | null;
  markup_pct: string;
  effective_from: string;
  /** null = current/active version. */
  effective_to: string | null;
  created_by: string | null;
  created_at: string;
  /** Video + image — the jsonb rate matrix. null/undefined on chat + embedding
   *  rows. Narrow with `isVideoRateTable` / `isImageRateTable` before reading
   *  kind-specific row fields. */
  rate_table?: LlmRateTable | null;
}

export interface LlmModel {
  id: string;
  kind: LlmModelKind;
  provider: string;
  provider_model_id: string;
  slug: string;
  display_name: string;
  description: string | null;
  capabilities: LlmModelCapabilities;
  context_window: number | null;
  default_max_output_tokens: number | null;
  max_output_tokens_hard_cap: number | null;
  is_public: boolean;
  is_active: boolean;
  sort_order: number;
  deprecated_at: string | null;
  replaced_by_slug: string | null;
  created_at: string;
  updated_at: string;
  /** Joined live row from llm_model_pricing (effective_to IS NULL) — list shape. */
  currentPricing?: LlmModelPricing;
  /** Video only — alternate public ids resolving to this model (e.g. ['seedance-2.0']). */
  aliases?: string[];
  /** Video only — provider endpoint routing. Admin-internal, NEVER exposed via V1. */
  endpoints?: { raw?: string; platform?: string };
}

export interface LlmModelDetail extends LlmModel {
  /** Detail shape: all pricing versions returned together, newest first. */
  llm_model_pricing: LlmModelPricing[];
}

export interface CreateLlmModelInitialPricing {
  input_usd_per_M?: number;
  output_usd_per_M?: number;
  cache_hit_usd_per_M?: number;
  input_text_usd_per_M?: number;
  input_visual_usd_per_M?: number;
  input_video_usd_per_M?: number;
  markup_pct: number;
  /** Video only — REQUIRED for kind=video (must fully cover capabilities.resolutions × video_input). */
  rate_table?: VideoRateTable;
}

export interface CreateLlmModelDto {
  /** Image rows are seeded by migration, not created here — see `CreatableLlmModelKind`. */
  kind: CreatableLlmModelKind;
  provider: string;
  provider_model_id: string;
  slug: string;
  display_name: string;
  description?: string;
  capabilities?: LlmModelCapabilities;
  /** Video only. Lowercase ^[a-z0-9.-]+$; globally unique across all model slugs + aliases. */
  aliases?: string[];
  /** Video only. Values must match ^ep-[a-z0-9-]+$. NEVER exposed to customers. */
  endpoints?: { raw?: string; platform?: string };
  context_window?: number;
  default_max_output_tokens?: number;
  max_output_tokens_hard_cap?: number;
  is_public?: boolean;
  is_active?: boolean;
  sort_order?: number;
  initial_pricing: CreateLlmModelInitialPricing;
}

export interface UpdateLlmModelDto {
  display_name?: string;
  description?: string;
  capabilities?: LlmModelCapabilities;
  /** Video only. Lowercase ^[a-z0-9.-]+$; globally unique across all model slugs + aliases. */
  aliases?: string[];
  /** Video only. Values must match ^ep-[a-z0-9-]+$. NEVER exposed to customers. */
  endpoints?: { raw?: string; platform?: string };
  context_window?: number;
  default_max_output_tokens?: number;
  max_output_tokens_hard_cap?: number;
  is_public?: boolean;
  is_active?: boolean;
  sort_order?: number;
  deprecated_at?: string | null;
  replaced_by_slug?: string | null;
}

export interface CreatePricingVersionDto {
  input_usd_per_M?: number;
  output_usd_per_M?: number;
  cache_hit_usd_per_M?: number;
  input_text_usd_per_M?: number;
  input_visual_usd_per_M?: number;
  input_video_usd_per_M?: number;
  markup_pct: number;
  /**
   * REQUIRED on every video AND image pricing version, re-validated server-side
   * (`admin-models.service.ts`). It is never inherited from the prior version:
   * a markup-only write that omits it is a 400, deliberately — the same request
   * shape previously stored `rate_table: NULL` and made every image generation
   * unpriceable. Forbidden on chat/embedding.
   */
  rate_table?: LlmRateTable;
  /** Image models: acknowledge that a tier prices below provider cost. Without
   *  it the server returns 400. Per-request, never persisted. */
  allow_below_cost?: boolean;
  effective_from?: string;
}

export interface LlmAnchor {
  usd_per_credit: number;
}

export interface UpdateAnchorDto {
  usd_per_credit: number;
  /** Server enforces minimum length 8. */
  reason: string;
}

// ─── Team LLM pricing overrides (admin) ─────────────────────────────────────
//
// Mirrors the JSONB document at `teams.llm_pricing_overrides`. Per-team
// markup default + per-model markup overrides + (optional) per-team credit
// value. See `apps/api/src/admin/team-pricing-overrides/dto/`.

export interface LlmPerModelOverride {
  /** Integer 0-1000 (founder #1 — non-integer markup rejected at write time). */
  markup_pct: number;
}

export interface LlmPricingOverridesDocument {
  /** Integer 0-1000. Applied to any model without a per-model override. */
  default_markup_pct?: number;
  /** Positive USD value. Replaces the global credit value when set. */
  credit_value_usd?: number;
  /** Per-model markup overrides keyed by model slug. */
  models?: Record<string, LlmPerModelOverride>;
}

export interface PutTeamPricingOverridesDto extends LlmPricingOverridesDocument {
  /** Audit reason. Server enforces min length 4. */
  reason: string;
}

export interface RevertTeamPricingOverridesDto {
  /** Audit reason. Server enforces min length 4. */
  reason: string;
}

/**
 * Per-team summary row returned by `GET /admin/models/:slug/team-overrides`.
 * Used by the model-first team-overrides tab.
 *
 * Embedding dimensions added 2026-05-24 (Layout C fix-forward). Chat fields
 * are NULL on embedding rows and vice versa — the model `kind` is known at
 * the page level (caller of `<ModelTeamOverridesTab>`).
 */
export interface TeamOverrideSummary {
  /**
   * Numeric team id (integer serial — `teams.id` is INTEGER, NOT a UUID
   * string). Backend DTO declares this as `number`; if you stringify it
   * for display, use `String(team_id)` — do NOT call string methods on it.
   */
  team_id: number;
  team_name: string;
  markup_pct_effective: number;
  source: 'model' | 'team_default' | 'model_default';
  // Chat fields — NULL on embedding rows
  cost_usd_per_M_input: number | null;
  cost_usd_per_M_output: number | null;
  end_usd_per_M_input: number | null;
  end_usd_per_M_output: number | null;
  credit_value_usd: number;
  customer_credits_per_M_input: number | null;
  customer_credits_per_M_output: number | null;
  // Embedding fields — NULL on chat rows. TWO modalities only (text + visual)
  // — the `video` modality was removed because BytePlus folds video_url input
  // into the visual billing bucket.
  cost_usd_per_M_input_text: number | null;
  cost_usd_per_M_input_visual: number | null;
  end_usd_per_M_input_text: number | null;
  end_usd_per_M_input_visual: number | null;
  customer_credits_per_M_input_text: number | null;
  customer_credits_per_M_input_visual: number | null;
  last_billed_at: string | null;
}

// ─── BytePlus catalog prefill (admin-only) ──────────────────────────────────
//
// Returned by GET /admin/models/byteplus-catalog. The backend already maps
// BytePlus's raw rows to Aurous-side suggestions — the frontend just renders
// them in a picker and feeds the selected entry into form.setValue().
//
// `suggested_kind: 'unknown'` covers image/video/3D generation rows we don't
// support in LLM PR-1. The dialog filters them out of the visible list.

export interface AurousCatalogEntry {
  provider_model_id: string;
  suggested_kind: 'chat' | 'embedding' | 'unknown';
  suggested_display_name: string;
  suggested_slug: string;
  suggested_capabilities: {
    multimodal_input?: boolean;
    tools?: boolean;
    structured_output?: boolean;
  };
  context_window: number | null;
  default_max_output_tokens: number | null;
  max_output_tokens_hard_cap: number | null;
  description: string | null;
  is_deprecated: boolean;
}

export interface ByteplusCatalogResponse {
  items: AurousCatalogEntry[];
  cached: boolean;
  fetched_at: string;
}

// ─── Image Actions (SP3 action-builder — /admin/loras/:id) ───────────────────
//
// Backend shapes are FINAL (admin.controller.ts `loras/:id/action-*` family +
// admin.service.ts toStillView/getActionSummary). Stills NEVER carry a raw
// storage_path — only a short-lived signed `thumbnail_url` (1h TTL).

export type ActionStillStatus = 'pending_review' | 'approved' | 'rejected';

/**
 * Per-still region-detection outcome (SP4 B6). Mirrors the backend's
 * `RegionsMeta` (inference/subject/still-regions.ts) field-for-field — keep
 * the two in sync.
 */
export interface RegionsMetaView {
  verdict: 'ok' | 'no_partner_visible' | 'no_clause' | 'failed' | 'malformed';
  model: string | null;
  prompt_version: number | null;
  confidence: number;
  detected_at: string;
  error?: string;
  partial_s1?: string;
  /** FD-3 minimal manual override (2026-07-21): admin user id when a human forced this verdict. */
  overridden_by?: string;
}

/** Curated `caption_meta` (toCaptionMetaView shape) — `axes` and `training_caption` stay server-side. */
export interface CaptionMetaView {
  /** null when caption_meta exists but captioning never ran (a hand-edit on a never-captioned still). */
  verdict: 'ok' | 'refusal' | 'malformed' | 'failed' | 'sign_failed' | null;
  model: string | null;
  prompt_version: number | null;
  captioned_at: string | null;
  error?: string;
  /** Admin user id when a human hand-edited the description. */
  edited_by?: string;
}

/** One `image_action_stills` row (toStillView shape). */
export interface ActionStill {
  id: string;
  description: string;
  /** null is a legitimate terminal state (classifier degraded or not-yet-run). */
  framing: string | null;
  character_count: number;
  featured: boolean;
  status: ActionStillStatus;
  ord: number;
  created_at: string;
  updated_at: string;
  /** Signed URL, 1h TTL. null when signing failed. */
  thumbnail_url: string | null;
  /** Detected boxes ("x1 y1 x2 y2", 0-999) keyed by role (s1/ref). Non-null only on verdict 'ok'. */
  regions: Record<string, string> | null;
  /** Detection provenance/verdict. null = detection has never run for this still. */
  regions_meta: RegionsMetaView | null;
  /**
   * Caption provenance/verdict. `null` = no caption_meta at all. A NON-null
   * value does NOT mean captioning ran — branch on `caption_meta?.verdict`,
   * never on `caption_meta !== null`.
   */
  caption_meta: CaptionMetaView | null;
}

/** Partial update body for a still (PATCH .../action-stills/:stillId). */
export interface PatchActionStillDto {
  description?: string;
  /** null CLEARS the framing — never send '' (the server 400s an empty string). */
  framing?: string | null;
  /** 1–4. */
  character_count?: number;
  featured?: boolean;
  status?: ActionStillStatus;
  ord?: number;
}

/** Result of a bulk status change (POST .../action-stills/bulk-status). */
export interface BulkStillStatusResult {
  /** Rows that actually changed (matched this LoRA). */
  updated: number;
  /** How many ids were sent. */
  requested: number;
  /** Ids NOT updated — nonexistent or owned by a different LoRA (never an error). */
  skipped: string[];
}

/** ZIP-ingest report (POST .../action-stills/ingest). */
export interface ActionStillIngestReport {
  /** Stills inserted as pending_review (dupes count under `rejected`, not here). */
  inserted: number;
  /** Each entry that did not land as pending_review, `machine_code: sentence`. */
  rejected: Array<{ entry: string; reason: string }>;
  /** Image entries considered (inserted + rejected). */
  total_entries: number;
  /** Entries inserted with an empty description — the grid must fill these in. */
  unlabeled: string[];
  /** Stills the post-ingest caption sweep queued. LoRA-wide, so it can exceed the number just uploaded. 0 when nothing was inserted, when the sweep had nothing to do, or when the sweep failed (never fatal to the ingest). */
  captioning_queued: number;
  /**
   * True when the sweep could not start because one was already running for
   * this LoRA. A re-run is queued and will pick these stills up automatically —
   * `captioning_queued` is 0 in this case and does NOT mean "nothing to do".
   */
  captioning_deferred: boolean;
}

/**
 * Which stills a caption sweep selected. Mirrors `CaptionScope` in
 * apps/api/src/admin/still-captions.service.ts.
 *
 * - `blank`   — stills with an empty description only.
 * - `missing` — never captioned, last attempt not ok, or written at an older
 *               prompt version.
 * - `all`     — every non-rejected still.
 */
export type CaptionScope = 'blank' | 'missing' | 'all';

/** Result of the bulk caption sweep (POST .../action-stills/caption). */
export interface CaptionSweepResult {
  /** Stills the sweep queued. Captioning trickles in server-side. */
  queued: number;
  /**
   * True when a sweep was already running for this LoRA, so this one was
   * coalesced into a re-run. `queued` is 0 here and does NOT mean "nothing to
   * do". Must be checked BEFORE `queued` and before `narrowed`.
   */
  deferred?: boolean;
  /** The scope the server actually ran, after its own clearance check. */
  scope: CaptionScope;
  /**
   * True when the server downgraded the scope it was asked for, because this act
   * is not cleared for re-captioning. `queued: 0` with `narrowed: true` means
   * every ELIGIBLE still already had text — stills that already carry a
   * description were never candidates, so it is NOT a statement about them.
   */
  narrowed: boolean;
}

/** One image `action_clauses` row (kind base|action; framing is always null here). */
export interface ImageActionClause {
  id: string;
  kind: 'base' | 'action';
  ord: number;
  framing: string | null;
  clause_text: string;
  character_count: number;
}

/**
 * What a caption sweep on this action would do and cost, right now. Mirrors the
 * `captioning` block in admin.service.ts `getActionSummary` field-for-field.
 *
 * Every field is one server-side resolution of the sweep's OWN predicate, so
 * the count on the button cannot drift from the rows a click queues.
 */
export interface ActionCaptioningSummary {
  /** The scope a body-less sweep would run, AFTER the server's clearance check. */
  scope: CaptionScope;
  /** True when this action's clearance downgraded that scope (blanks only). */
  narrowed: boolean;
  /** Rows the sweep would queue RIGHT NOW. Decays as a running sweep lands captions. */
  queueable: number;
  /** The caption prompt version the server is on — the input `isAwaitingHuman` cannot derive. */
  prompt_version: number;
  /** Present (and true) only when `queueable` hit the server's row cap, so the real figure is higher. */
  queueable_capped?: boolean;
}

/** Per-count support derivation + tallies (GET .../action-summary, spec §6). */
export interface ActionSummary {
  /** Counts N with ≥1 approved still AND ≥1 valid action clause at N. Authoritative. */
  supported_character_counts: number[];
  live: boolean;
  stills: { approved: number; pending: number; rejected: number };
  /** Valid action clauses per character_count. */
  clauses_by_count: Record<number, number>;
  /**
   * OPTIONAL, and its ABSENCE is meaningful: the server omits the whole key
   * when the count fails, rather than zero-filling it. `queueable: 0` renders
   * as "Nothing to caption", a confident lie about an act that may have
   * hundreds of blank stills; an absent block renders the countless,
   * still-enabled button. Absence means "unknown", and only absence can.
   */
  captioning?: ActionCaptioningSummary;
}

export const adminService = {
  async getTeams(filters: AdminTeamFilters = {}): Promise<AdminTeam[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.phase) params.set('phase', filters.phase);
    const qs = params.toString();
    const response = await apiClient.get<AdminTeam[]>(`/admin/teams${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  async getUsers(filters: AdminUserFilters = {}): Promise<AdminUser[]> {
    const params = new URLSearchParams();
    if (filters.source) params.set('source', filters.source);
    const qs = params.toString();
    const response = await apiClient.get<AdminUser[]>(`/admin/users${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  async updateUserRole(userId: string, platformRole: 'user' | 'platform_admin' | 'debugger'): Promise<AdminUser> {
    const response = await apiClient.patch<AdminUser>(`/admin/users/${userId}/role`, { platform_role: platformRole });
    return response.data;
  },

  /**
   * Closed-beta gate: number of teams in `pending`. Used by /admin/teams to
   * label the "Pending" filter chip and by the sidebar Teams nav badge.
   * Backend returns a wrapped envelope `{ statusCode, type, data: { count } }`
   * to match the rest of the v1 surface.
   */
  async getPendingCount(): Promise<number> {
    const response = await apiClient.get<PendingCountResponse>('/admin/teams/pending-count');
    return response.data.data.count;
  },

  /**
   * Live phase split (testing vs active) for the /admin/teams phase filter
   * chips. Sibling of getPendingCount — same wrapped envelope shape. Caller
   * invalidates ['admin-phase-counts'] whenever a phase flip succeeds.
   */
  async getPhaseCounts(): Promise<{ testing: number; active: number }> {
    const response = await apiClient.get<PhaseCountsResponse>('/admin/teams/phase-counts');
    return response.data.data;
  },

  /** Support-view credit trio + runway inputs for /admin/teams/:id. */
  async getTeamBalance(teamId: number): Promise<AdminTeamBalance> {
    const response = await apiClient.get<AdminTeamBalance>(`/admin/teams/${teamId}/balance`);
    return response.data;
  },

  /**
   * Approve a pending team. Grants `credits` and optionally appends `customNote`
   * to the approval email body. Caller should invalidate ['admin-teams'] +
   * ['admin-pending-count'] on success.
   */
  async approveTeam(teamId: number, payload: ApproveTeamPayload) {
    const response = await apiClient.post(`/admin/teams/${teamId}/approve`, payload);
    return response.data;
  },

  /**
   * Create a new approved team for a customer email and send a Resend
   * magic-link invite. Used by the /admin/teams "Create team" dialog to
   * pre-provision teams for customers who haven't signed up yet — they
   * land in the dashboard with credits already on the balance.
   */
  async createTeam(payload: CreateTeamPayload): Promise<CreateTeamResponse> {
    const response = await apiClient.post<{ data: CreateTeamResponse }>('/admin/teams', payload);
    return response.data.data;
  },

  /** Reject a pending team. `privateReason` is audit-only — never emailed. */
  async rejectTeam(teamId: number, payload: RejectTeamPayload) {
    const response = await apiClient.post(`/admin/teams/${teamId}/reject`, payload);
    return response.data;
  },

  /** Suspend an already-approved team. `privateReason` is audit-only — never emailed. */
  async suspendTeam(teamId: number, payload: SuspendTeamPayload) {
    const response = await apiClient.post(`/admin/teams/${teamId}/suspend`, payload);
    return response.data;
  },

  /** Move a rejected/suspended team back to `pending`. */
  async reinstateTeam(teamId: number) {
    const response = await apiClient.post(`/admin/teams/${teamId}/reinstate`);
    return response.data;
  },

  /**
   * Change a team's content mode at any point in the lifecycle (approved,
   * grandfathered, and suspended teams are all valid targets — not gated on
   * account_status). `reason` is mandatory (server enforces >=4 chars) and is
   * recorded in the `team.content_mode_changed` audit row — never emailed.
   * Caller should invalidate ['admin-teams'] on success.
   */
  async setContentMode(teamId: number, payload: { contentMode: 'safe' | 'nsfw'; reason: string }) {
    const response = await apiClient.post(`/admin/teams/${teamId}/content-mode`, payload);
    return response.data;
  },

  async getLoras(): Promise<AdminLora[]> {
    const response = await apiClient.get<AdminLora[]>('/admin/loras');
    return response.data;
  },

  async getAllLoras(): Promise<AdminLora[]> {
    const response = await apiClient.get<AdminLora[]>('/admin/loras/all');
    return response.data;
  },

  async getInvoices(): Promise<AdminInvoice[]> {
    const response = await apiClient.get<AdminInvoice[]>('/admin/invoices');
    return response.data;
  },

  /**
   * Close a crypto payment Inqud reported as a conflict. Audited server-side.
   * `grantSecondTrial` is the operator's explicit consent to credit a team
   * that already used its one-time trial; without it the server answers 409.
   */
  async resolvePayment(
    paymentId: number,
    resolution: PaymentResolution,
    note: string,
    grantSecondTrial = false,
  ): Promise<{ id: number; status: string }> {
    const response = await apiClient.post<{ id: number; status: string }>(
      `/admin/payments/${paymentId}/resolve`,
      { resolution, note, ...(grantSecondTrial ? { grantSecondTrial: true } : {}) },
    );
    return response.data;
  },

  async getLumiLoras(): Promise<LumiLoraItem[]> {
    const response = await apiClient.get<LumiLoraItem[]>('/admin/lumi/loras');
    return response.data;
  },

  async createPublicLora(data: CreatePublicLoraDto): Promise<AdminLora> {
    const response = await apiClient.post<AdminLora>('/admin/loras', data);
    return response.data;
  },

  async updatePublicLora(loraId: string, data: Partial<CreatePublicLoraDto>): Promise<AdminLora> {
    const response = await apiClient.patch<AdminLora>(`/admin/loras/${loraId}`, data);
    return response.data;
  },

  async deletePublicLora(loraId: string): Promise<void> {
    await apiClient.delete(`/admin/loras/${loraId}`);
  },

  /**
   * Toggle a public LoRA into / out of the curated `featured_rank` list shown
   * to brand-new users on first run. Pass a positive integer to set a rank,
   * or `null` to unfeature.
   */
  async setFeaturedRank(loraId: string, rank: number | null): Promise<void> {
    await apiClient.patch(`/admin/loras/${loraId}/featured`, { rank });
  },

  /**
   * Returns MAX(featured_rank) + 1 across all LoRAs (or 1 if none are
   * featured). Called by the Featured Switch before flipping ON so the new
   * entry appends to the curated list.
   */
  async getNextFeaturedRank(): Promise<number> {
    const response = await apiClient.get<{ rank: number }>('/admin/loras/featured/next-rank');
    return response.data.rank;
  },

  // ─── Style registers (PR-2 Task 1) ──────────────────────────────────────
  //
  // `updateLoraRegister`/`getStyleDispatchSettings`/`setStyleDispatchSettings`
  // consume PR-1 endpoints, verified live on this branch
  // (apps/api/src/admin/admin.controller.ts + style-admin.service.ts).
  // `testRenderRegister` defines the wire contract for the endpoint Task 5 of
  // this plan (docs/superpowers/plans/2026-07-24-style-registers-pr2-ui.md)
  // adds — it 404s until that task ships the backend route.

  /**
   * Set or clear a LoRA's style register (PATCH /admin/loras/:id/register).
   * Every call is a FULL REPLACE — `register_prompt: null` clears it, there
   * is no partial-update state (mirrors the backend's `UpdateRegisterDto`).
   * The response body is `{ register_prompt }`, but the Register tab's
   * diff-confirm flow (design-notes.md §3.2) already holds the published
   * text locally and invalidates `['admin-loras']` on success, so the reply
   * is discarded here — same pattern as `setFeaturedRank` above.
   */
  async updateLoraRegister(id: string, register_prompt: string | null): Promise<void> {
    await apiClient.patch(`/admin/loras/${id}/register`, { register_prompt });
  },

  /**
   * Current style-dispatcher kill-switch state
   * (`platform_settings['style_dispatch_enabled']`). Defaults to enabled
   * when the row is unset.
   */
  async getStyleDispatchSettings(): Promise<{ enabled: boolean }> {
    const response = await apiClient.get<{ enabled: boolean }>('/admin/dispatcher/style-settings');
    return response.data;
  },

  /**
   * Flip the style-dispatcher kill switch. When disabled, every generation
   * that would have auto-matched a style instead records
   * `dispatcher_outcome='off'` and proceeds without a style. Audited
   * server-side. Route is PUT, not PATCH — matches
   * `admin.controller.ts`'s `@Put('dispatcher/style-settings')`.
   */
  async setStyleDispatchSettings(enabled: boolean): Promise<void> {
    await apiClient.put('/admin/dispatcher/style-settings', { enabled });
  },

  /**
   * Render the CURRENT TEXTAREA content of a register edit — including
   * unsaved changes — against a test prompt. Not billed, not saved to any
   * team, no inference row created (design-notes.md §3.3). `image_url` is
   * `null` when the provider call fails; the enhanced-prompt half of the
   * result is still useful on its own, so a null image is not an error.
   * Backend lands in Task 5 of this plan — this method is the pre-agreed
   * contract the implementer builds against.
   */
  async testRenderRegister(body: { register_prompt: string; prompt: string }): Promise<{ enhanced_prompt: string; image_url: string | null }> {
    const response = await apiClient.post<{ enhanced_prompt: string; image_url: string | null }>(
      '/admin/loras/register/test-render',
      body,
    );
    return response.data;
  },

  async adjustTeamCredits(teamId: number, amount: number, reason: string): Promise<CreditAdjustmentResult> {
    const response = await apiClient.post<CreditAdjustmentResult>(`/admin/teams/${teamId}/credits`, { amount, reason });
    return response.data;
  },

  async setTeamDiscounts(teamId: number, data: { inference_discount_pct?: number | null; purchase_discount_pct?: number | null; reason: string }) {
    const response = await apiClient.patch(`/admin/teams/${teamId}/discounts`, data);
    return response.data;
  },

  async getPricing(): Promise<PricingConfig[]> {
    const response = await apiClient.get<PricingConfig[]>('/admin/pricing');
    return response.data;
  },

  /**
   * `reason` (≤ 500 chars) is written to the audit row. It has always been
   * optional on the DTO and the console never sent it, so every historical
   * pricing edit reads as `reason: null` in /admin/audit; omit it only for
   * callers that genuinely have no operator behind the change.
   */
  async updatePricing(key: string, value: number, reason?: string): Promise<void> {
    // Path uses /admin/pricing/key/:key — the bare /admin/pricing/:key shape
    // shadowed PATCH /admin/pricing/anchor (anchor matched as key) which
    // silently no-op'd because the new DTO ships usd_per_credit, not value.
    await apiClient.patch(`/admin/pricing/key/${encodeURIComponent(key)}`, {
      value,
      ...(reason === undefined ? {} : { reason }),
    });
  },

  async getAdminCreditPackages() {
    const response = await apiClient.get<Array<{ id: number; name: string; credits: number; price_cents: number; currency: string; is_active: boolean; sort_order: number; created_at: string }>>('/admin/packages');
    return response.data;
  },

  async createCreditPackage(data: { name: string; credits: number; price_cents: number; sort_order?: number }) {
    const response = await apiClient.post('/admin/packages', data);
    return response.data;
  },

  async updateCreditPackage(id: number, data: { name?: string; credits?: number; price_cents?: number; is_active?: boolean; sort_order?: number }) {
    const response = await apiClient.patch(`/admin/packages/${id}`, data);
    return response.data;
  },

  async refreshAutoTopUpRates() {
    const response = await apiClient.post<{ updated: number }>('/admin/refresh-auto-topup-rates');
    return response.data;
  },

  async deliverLora(data: DeliverLoraDto): Promise<AdminLora[]> {
    const response = await apiClient.post<AdminLora[]>('/admin/loras/deliver', data);
    return response.data;
  },

  async getDeliveriesForLora(loraId: string): Promise<LoraDelivery[]> {
    const response = await apiClient.get<LoraDelivery[]>(`/admin/loras/${loraId}/deliveries`);
    return response.data;
  },

  async getPromptTemplates(): Promise<PromptTemplate[]> {
    const response = await apiClient.get<PromptTemplate[]>('/admin/prompt-templates');
    return response.data;
  },

  async createPromptTemplate(data: { name: string; template_text: string; description?: string }): Promise<PromptTemplate> {
    const response = await apiClient.post<PromptTemplate>('/admin/prompt-templates', data);
    return response.data;
  },

  async updatePromptTemplate(id: string, data: Partial<{ name: string; template_text: string; description?: string }>): Promise<PromptTemplate> {
    const response = await apiClient.patch<PromptTemplate>(`/admin/prompt-templates/${id}`, data);
    return response.data;
  },

  async deletePromptTemplate(id: string): Promise<void> {
    await apiClient.delete(`/admin/prompt-templates/${id}`);
  },

  // Bundle management
  async getBundles(): Promise<AdminBundle[]> {
    const response = await apiClient.get<AdminBundle[]>('/admin/bundles');
    return response.data;
  },

  async createBundle(data: CreateBundleDto): Promise<AdminBundle> {
    const response = await apiClient.post<AdminBundle>('/admin/bundles', data);
    return response.data;
  },

  async updateBundle(id: string, data: Partial<CreateBundleDto>): Promise<AdminBundle> {
    const response = await apiClient.patch<AdminBundle>(`/admin/bundles/${id}`, data);
    return response.data;
  },

  async deleteBundle(id: string): Promise<void> {
    await apiClient.delete(`/admin/bundles/${id}`);
  },

  async deliverBundle(data: DeliverBundleDto): Promise<AdminBundle[]> {
    const response = await apiClient.post<AdminBundle[]>('/admin/bundles/deliver', data);
    return response.data;
  },

  // ─── Video LoRAs ──────────────────────────────────────────────────────────

  async getVideoLoras(): Promise<AdminVideoLora[]> {
    const response = await apiClient.get<AdminVideoLora[]>('/admin/video-loras');
    return response.data;
  },

  async createVideoLora(data: CreateVideoLoraDto): Promise<AdminVideoLora> {
    const response = await apiClient.post<AdminVideoLora>('/admin/video-loras', data);
    return response.data;
  },

  /**
   * `is_active`, `reason` (audit note, not a column — controller strips it
   * before the UPDATE), and the `default_*` fields aren't on `CreateVideoLoraDto`
   * (the create dialog never sets them; the backend's create DTO accepts them
   * but nothing in the UI sends them at create time) — extended here to match
   * `admin.service.ts updateVideoLora`'s actual accepted body one-for-one.
   */
  async updateVideoLora(
    id: string,
    data: Partial<Omit<CreateVideoLoraDto, 'team_id' | 'grant_team_ids'>> & {
      is_active?: boolean;
      default_resolution?: string;
      default_ratio?: string;
      default_duration?: number;
      reason?: string;
      /** Full-replace of the structured clause set; [] clears it (server re-syncs prompt_template_text). */
      clauses?: Array<Pick<ActionClauseRow, 'kind' | 'framing' | 'clause_text'>>;
    },
  ): Promise<AdminVideoLora> {
    const response = await apiClient.patch<AdminVideoLora>(`/admin/video-loras/${id}`, data);
    return response.data;
  },

  async deleteVideoLora(id: string): Promise<void> {
    await apiClient.delete(`/admin/video-loras/${id}`);
  },

  /** Action (detail) — row + clip_counts + grant_team_ids + grant_teams. 404 when missing. */
  async getVideoLora(id: string): Promise<AdminVideoLoraDetail> {
    const response = await apiClient.get<AdminVideoLoraDetail>(`/admin/video-loras/${id}`);
    return response.data;
  },

  /** Idempotent replace of an Action's team grants. */
  async setVideoLoraGrants(id: string, team_ids: number[]): Promise<{ grant_team_ids: number[] }> {
    const response = await apiClient.put<{ grant_team_ids: number[] }>(
      `/admin/video-loras/${id}/grants`,
      { team_ids },
    );
    return response.data;
  },

  /** Field-level change history for an Action (`lora_audit` rows), newest first. */
  async getVideoLoraAudit(id: string): Promise<AdminVideoLoraAuditRow[]> {
    const response = await apiClient.get<AdminVideoLoraAuditRow[]>(`/admin/video-loras/${id}/audit`);
    return response.data;
  },

  /** Structural verdict + all-subsets previews for an Action clause set (no DB writes). */
  async validateActionClauses(
    clauses: Array<Pick<ActionClauseRow, 'kind' | 'framing' | 'clause_text'>>,
  ): Promise<ClauseValidationResponse> {
    const response = await apiClient.post<ClauseValidationResponse>('/admin/video-loras/validate-clauses', {
      clauses,
    });
    return response.data;
  },

  // ─── Action Clips (clip intake — the scraper seam) ────────────────────────

  /**
   * Uploads a single clip reference file (mp4/mov, <=50MB, validated server-side)
   * to the private bucket. Returns storage_path only — does NOT create a
   * template_clips row. Pair with registerVideoLoraClip to attach it to an Action.
   */
  async uploadVideoLoraClip(file: File): Promise<{ storage_path: string }> {
    const formData = new FormData();
    formData.append('file', file);
    // _silent: errors are surfaced by the call-site UI per-row (clip staging);
    // suppress the global interceptor toast to avoid duplication.
    const response = await apiClient.post<{ storage_path: string }>(
      '/admin/video-loras/upload-clip',
      formData,
      { _silent: true } as Parameters<typeof apiClient.post>[2],
    );
    return response.data;
  },

  /** An Action's clips, ord asc, each merged with its live provider-asset status + a signed preview URL. */
  async listVideoLoraClips(id: string): Promise<AdminActionClip[]> {
    const response = await apiClient.get<AdminActionClip[]>(`/admin/video-loras/${id}/clips`);
    return response.data;
  },

  /** Registers an already-uploaded storage_path as a clip on an Action; appended at the end, kicks off async ingestion. */
  async registerVideoLoraClip(
    id: string,
    body: {
      storage_path: string;
      description: string;
      featured?: boolean;
      /**
       * Optional in-browser-trim provenance (W2 PR-3 fix-forward). Omit
       * entirely for a raw/untrimmed upload — these are recorded in the
       * `clip_added` audit entry only, no DB column, so there's nothing to
       * default server-side and no reason to send them when false.
       */
      trimmed?: boolean;
      engine?: 'webcodecs' | 'ffmpeg';
      trim_range?: [number, number];
    },
  ): Promise<{ id: string; description: string; featured: boolean; is_active: boolean; ord: number; clip_status: 'pending' }> {
    const response = await apiClient.post<{
      id: string; description: string; featured: boolean; is_active: boolean; ord: number; clip_status: 'pending';
    }>(`/admin/video-loras/${id}/clips`, body);
    return response.data;
  },

  async patchVideoLoraClip(
    id: string,
    clipId: string,
    patch: {
      description?: string;
      featured?: boolean;
      is_active?: boolean;
      /**
       * 0-BASED TARGET INDEX among the Action's clips (active + inactive), NOT
       * a raw `ord` delta. The server splices the clip out of its current
       * position, reinserts it at this index, then renumbers 0..n-1 across the
       * sibling set — always send the destination position, e.g. dragging a
       * clip to the top of the list sends `ord: 0`.
       */
      ord?: number;
    },
  ): Promise<unknown> {
    const response = await apiClient.patch(`/admin/video-loras/${id}/clips/${clipId}`, patch);
    return response.data;
  },

  /** Soft-delete (is_active=false) — generations stop using the clip immediately. */
  async deleteVideoLoraClip(id: string, clipId: string): Promise<{ success: true }> {
    const response = await apiClient.delete<{ success: true }>(`/admin/video-loras/${id}/clips/${clipId}`);
    return response.data;
  },

  /** Retry a single clip. Allowed when its live status is failed OR stale-pending (409 otherwise). */
  async retryVideoLoraClipAsset(id: string, clipId: string): Promise<{ clip_status: 'pending' }> {
    const response = await apiClient.post<{ clip_status: 'pending' }>(
      `/admin/video-loras/${id}/clips/${clipId}/retry-asset`,
    );
    return response.data;
  },

  /** Retries every retryable (failed / stale-pending) active clip on an Action. */
  async retryAllFailedClips(id: string): Promise<{ retried: number }> {
    const response = await apiClient.post<{ retried: number }>(`/admin/video-loras/${id}/clips/retry-failed`);
    return response.data;
  },

  /**
   * Renormalizes an Action's clip `ord` values to a dense 0..n-1 sequence —
   * fixes duplicate ords left behind by concurrent registers. Background
   * housekeeping fired after a bulk-ingest queue drains (F10), not a
   * user-initiated action — silent like the health poll, no toast either way.
   */
  async normalizeVideoLoraClipOrds(id: string): Promise<{ normalized: number }> {
    const response = await apiClient.post<{ normalized: number }>(
      `/admin/video-loras/${id}/clips/normalize-ords`,
      undefined,
      { _silent: true } as Parameters<typeof apiClient.post>[2],
    );
    return response.data;
  },

  // ─── Provider Asset Health ─────────────────────────────────────────────────

  /** Platform-wide provider_assets snapshot — drives the Actions sidebar dot + ops dashboard. */
  async getProviderAssetsHealth(): Promise<ProviderAssetsHealth> {
    const response = await apiClient.get<ProviderAssetsHealth>(
      '/admin/provider-assets/health',
      // Polled every 60s in the background — don't toast on transient failures.
      { _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  // ─── Character moderation ─────────────────────────────────────────────────

  async listCharacters(params: AdminCharacterListParams = {}): Promise<AdminCharacterListResponse> {
    // Silence the global toast — the page surfaces its own banner so we don't
    // re-toast on every retry / window-focus refetch with the same filters.
    const response = await apiClient.get<AdminCharacterListResponse>(
      '/admin/characters',
      { params, _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  async getCharacterUsage(id: string, params: { page?: number; limit?: number } = {}): Promise<AdminUsageListResponse> {
    const response = await apiClient.get<AdminUsageListResponse>(`/admin/characters/${id}/usage`, { params });
    return response.data;
  },

  async softDeleteCharacter(id: string, reason: string): Promise<void> {
    await apiClient.delete(`/admin/characters/${id}`, { data: { reason } });
  },

  async undeleteCharacter(id: string): Promise<void> {
    await apiClient.post(`/admin/characters/${id}/undelete`);
  },

  async setCharacterReviewed(id: string, reviewed: boolean): Promise<void> {
    await apiClient.post(`/admin/characters/${id}/moderation-reviewed`, { reviewed });
  },

  // Silenced like listCharacters: the drawer is open over the toast region,
  // so a failed fetch has to surface inside the tab body or it is a dropped
  // error.
  async listCharacterCharges(id: string): Promise<AdminCharacterChargesResponse> {
    const response = await apiClient.get<AdminCharacterChargesResponse>(
      `/admin/characters/${id}/charges`,
      { _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  async listCharacterAttempts(id: string): Promise<AdminCharacterAttemptsResponse> {
    const response = await apiClient.get<AdminCharacterAttemptsResponse>(
      `/admin/characters/${id}/attempts`,
      { _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  async listReferenceAttempts(
    params: AdminReferenceAttemptListParams = {},
  ): Promise<AdminReferenceAttemptListResponse> {
    const response = await apiClient.get<AdminReferenceAttemptListResponse>(
      '/admin/characters/attempts',
      { params, _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  // ─── Audit log ────────────────────────────────────────────────────────────

  async listAudit(params: AdminAuditListParams = {}): Promise<AdminAuditListResponse> {
    const response = await apiClient.get<AdminAuditListResponse>('/admin/audit', { params });
    return response.data;
  },

  // ─── Character settings (synthesis-LoRA + per-LoRA synthesis profile) ─────

  async getCurrentSynthesisLora(): Promise<SynthesisLoraInfo | null> {
    const response = await apiClient.get<SynthesisLoraInfo | null>('/admin/character-settings/synthesis-lora');
    return response.data;
  },

  async listEligibleSynthesisLoras(): Promise<SynthesisLoraInfo[]> {
    const response = await apiClient.get<SynthesisLoraInfo[]>('/admin/character-settings/eligible-loras');
    return response.data;
  },

  async setSynthesisLora(loraId: string, reason?: string): Promise<SynthesisFlipResult> {
    const response = await apiClient.post<SynthesisFlipResult>(
      '/admin/character-settings/synthesis-lora',
      { lora_id: loraId, reason },
    );
    return response.data;
  },

  async updateSynthesisProfile(loraId: string, settings: Record<string, unknown>) {
    const response = await apiClient.patch(`/admin/character-settings/synthesis-profile/${loraId}`, settings);
    return response.data;
  },

  async setSynthesisMode(mode: SynthesisMode, reason: string): Promise<SynthesisModeFlipResult> {
    const response = await apiClient.patch<SynthesisModeFlipResult>(
      '/admin/character-settings/synthesis-mode',
      { mode, reason },
    );
    return response.data;
  },

  async testSynthesisRender(loraId: string) {
    const response = await apiClient.post(`/admin/character-settings/test-render/${loraId}`);
    return response.data;
  },

  // ─── Team synthesis-pause toggle ──────────────────────────────────────────

  async toggleTeamSynthesisPaused(teamId: number, paused: boolean, reason?: string) {
    const response = await apiClient.post(
      `/admin/teams/${teamId}/synthesis-pause`,
      { paused, reason },
    );
    return response.data as { ok: boolean };
  },

  // ─── Team billing-exempt toggle ───────────────────────────────────────────

  /**
   * Mark a team billing-exempt (exempt=true) or resume normal metered billing
   * (exempt=false). A `reason` is REQUIRED in BOTH directions (server enforces
   * 4–500 chars — both moving money) and is logged to admin_audit_logs as
   * `team.billing_exempt.toggle`. Caller invalidates ['admin-teams'] +
   * ['admin-audit'] on success.
   */
  async toggleTeamBillingExempt(teamId: number, exempt: boolean, reason: string) {
    const response = await apiClient.patch(
      `/admin/teams/${teamId}/billing-exempt`,
      { exempt, reason },
    );
    return response.data as { ok: boolean; billing_exempt: boolean };
  },

  /** Flip a team's inference phase. Caller invalidates ['admin-teams']. */
  async setTeamPhase(teamId: number, phase: TeamPhase) {
    const response = await apiClient.patch(`/admin/teams/${teamId}/phase`, { phase });
    return response.data as { ok: boolean; phase: TeamPhase };
  },

  // ─── Inference lookup (support drilldown) ────────────────────────────────

  async getInferenceById(id: string): Promise<AdminInferenceDetail> {
    const response = await apiClient.get<AdminInferenceDetail>(
      `/admin/inferences/${encodeURIComponent(id)}`,
      // The lookup page renders 404 / 500 in-page — keep the global toast quiet.
      { _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  /**
   * Paginated, filterable list of one team's generations for the Team ▸ Content
   * tab. Returns LIGHT rows (tile-level fields); call getInferenceById for the
   * full forensic detail of a single generation.
   */
  async listTeamInferences(
    teamId: number,
    params: ListTeamInferencesParams = {},
  ): Promise<AdminTeamInferenceListResponse> {
    // _silent: the Content tab renders its own error state; don't double-toast.
    const response = await apiClient.get<AdminTeamInferenceListResponse>(
      `/admin/teams/${teamId}/inferences`,
      { params, _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  /**
   * Filter-reactive KPI aggregate for the Content tab. Applies the SAME filters
   * as listTeamInferences (minus page/limit). `_silent`: the strip owns its own
   * per-card error state, so don't double-toast.
   */
  async getTeamInferenceStats(
    teamId: number,
    params: Omit<ListTeamInferencesParams, 'page' | 'limit'> = {},
  ): Promise<TeamInferenceStats> {
    const response = await apiClient.get<TeamInferenceStats>(
      `/admin/teams/${teamId}/inference-stats`,
      { params, _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  /**
   * LoRA + API-key dropdown options for the Content tab filter ribbon. `_silent`:
   * the ribbon degrades gracefully (hides the affected select) on failure.
   */
  async getTeamContentFilterOptions(teamId: number): Promise<TeamContentFilterOptions> {
    const response = await apiClient.get<TeamContentFilterOptions>(
      `/admin/teams/${teamId}/content-filter-options`,
      { _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  // ─── Storage health (cutover monitoring) ─────────────────────────────────

  async getStorageHealth(): Promise<StorageHealthResponse> {
    const response = await apiClient.get<StorageHealthResponse>(
      '/admin/storage/health',
      // Polled every 60s in the background — don't toast on transient failures.
      { _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  // ─── LLM Models (admin CRUD) ─────────────────────────────────────────────

  /**
   * List LLM models for the /admin/models table. Pass includeInactive=true to
   * also surface deactivated + non-public rows — the backend gates inactive
   * rows behind this flag so the Show inactive toggle on the admin table can
   * actually reach them (previously a client-side filter on top of an
   * active-only response = nothing to show). Response is wrapped in
   * `{ items }` — we unwrap here so callers get a flat array.
   */
  async getLlmModels(opts?: { kind?: LlmModelKind; includeInactive?: boolean }): Promise<LlmModel[]> {
    const params = new URLSearchParams();
    if (opts?.kind) params.set('kind', opts.kind);
    if (opts?.includeInactive) params.set('include_inactive', '1');
    const qs = params.toString();
    const response = await apiClient.get<{ items: LlmModel[] }>(
      `/admin/models${qs ? `?${qs}` : ''}`,
    );
    return response.data.items ?? [];
  },

  async getLlmModel(id: string): Promise<LlmModelDetail> {
    const response = await apiClient.get<LlmModelDetail>(`/admin/models/${id}`);
    return response.data;
  },

  async createLlmModel(dto: CreateLlmModelDto): Promise<LlmModel> {
    const response = await apiClient.post<LlmModel>('/admin/models', dto);
    return response.data;
  },

  async updateLlmModel(id: string, dto: UpdateLlmModelDto): Promise<LlmModel> {
    const response = await apiClient.patch<LlmModel>(`/admin/models/${id}`, dto);
    return response.data;
  },

  /** Soft delete — sets is_active=false; row is never hard-deleted. */
  async deleteLlmModel(id: string): Promise<void> {
    await apiClient.delete(`/admin/models/${id}`);
  },

  /**
   * Create a new pricing version. Backend closes the prior row's effective_to
   * and bumps llm_rates_revision atomically (best-effort).
   */
  async createLlmModelPricing(
    modelId: string,
    dto: CreatePricingVersionDto,
  ): Promise<LlmModelPricing> {
    const response = await apiClient.post<LlmModelPricing>(
      `/admin/models/${modelId}/pricing`,
      dto,
    );
    return response.data;
  },

  /** All pricing versions for a model, newest version first. Wrapped in `{ items }`. */
  async getLlmModelPricingHistory(modelId: string): Promise<LlmModelPricing[]> {
    const response = await apiClient.get<{ items: LlmModelPricing[] }>(
      `/admin/models/${modelId}/pricing/history`,
    );
    return response.data.items ?? [];
  },

  // ─── LLM Anchor (USD per credit) ─────────────────────────────────────────

  /** The metered-AI settings (extra markup, minimum charge, output cap per feature), with their ranges. */
  async getMeteredAiSettings(): Promise<MeteredAiSetting[]> {
    const response = await apiClient.get<{ settings: MeteredAiSetting[] }>('/admin/pricing/metered-ai');
    return response.data.settings;
  },

  /** Range-checked and audit-logged; reason must be >= 8 chars (server enforces). */
  async updateMeteredAiSetting(key: string, value: number, reason: string): Promise<MeteredAiSetting> {
    const response = await apiClient.patch<MeteredAiSetting>(`/admin/pricing/metered-ai/${encodeURIComponent(key)}`, { value, reason });
    return response.data;
  },

  async getLlmAnchor(): Promise<LlmAnchor> {
    const response = await apiClient.get<LlmAnchor>('/admin/pricing/anchor');
    return response.data;
  },

  /** Audit-logged. reason must be >=8 chars (server enforces). */
  async updateLlmAnchor(dto: UpdateAnchorDto): Promise<LlmAnchor> {
    const response = await apiClient.patch<LlmAnchor>('/admin/pricing/anchor', dto);
    return response.data;
  },

  // ─── BytePlus catalog prefill (admin /admin/models/new) ─────────────────
  //
  // Server caches the upstream BytePlus response for 60s — staleTime on the
  // useQuery should match so the dialog opens instantly on the second click.

  async getByteplusCatalog(): Promise<ByteplusCatalogResponse> {
    const response = await apiClient.get<ByteplusCatalogResponse>(
      '/admin/models/byteplus-catalog',
    );
    return response.data;
  },

  // ─── Team LLM pricing overrides (admin only) ─────────────────────────────
  //
  // GET returns the current JSONB document (`{}` when empty).
  // PUT replaces the entire document — last-write-wins (founder cut ETag).
  // POST .../revert-last replays the most recent audit before-snapshot.
  // GET /admin/models/:slug/team-overrides returns per-team summary rows.

  async getTeamPricingOverrides(teamId: string | number): Promise<LlmPricingOverridesDocument> {
    const response = await apiClient.get<LlmPricingOverridesDocument>(
      `/admin/teams/${teamId}/pricing-overrides`,
    );
    return response.data;
  },

  async putTeamPricingOverrides(
    teamId: string | number,
    dto: PutTeamPricingOverridesDto,
  ): Promise<LlmPricingOverridesDocument> {
    const response = await apiClient.put<LlmPricingOverridesDocument>(
      `/admin/teams/${teamId}/pricing-overrides`,
      dto,
    );
    return response.data;
  },

  async revertTeamPricingOverrides(
    teamId: string | number,
    dto: RevertTeamPricingOverridesDto,
  ): Promise<LlmPricingOverridesDocument> {
    const response = await apiClient.post<LlmPricingOverridesDocument>(
      `/admin/teams/${teamId}/pricing-overrides/revert-last`,
      dto,
    );
    return response.data;
  },

  async listModelTeamOverrides(slug: string): Promise<TeamOverrideSummary[]> {
    const response = await apiClient.get<{ items: TeamOverrideSummary[] }>(
      `/admin/models/${slug}/team-overrides`,
    );
    return response.data.items ?? [];
  },

  // ─── Image Actions (SP3 action-builder — /admin/loras/:id) ───────────────

  /**
   * Single multipart POST of one training-set ZIP → an IngestReport. NOT a
   * per-file queue. `onProgress` drives the determinate bar (0–100); `signal`
   * lets the caller abort. `_silent`: the upload panel renders its own inline
   * errors + the thrown archive-level 400 (archive_too_large / too_many_entries
   * / archive_unreadable), so the global interceptor toast would double up.
   */
  async ingestActionStills(
    loraId: string,
    file: File,
    opts?: { onProgress?: (pct: number) => void; signal?: AbortSignal },
  ): Promise<ActionStillIngestReport> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<ActionStillIngestReport>(
      `/admin/loras/${loraId}/action-stills/ingest`,
      formData,
      {
        _silent: true,
        signal: opts?.signal,
        onUploadProgress: opts?.onProgress
          ? (event: { loaded: number; total?: number }) => {
              if (event.total) opts.onProgress!(Math.round((event.loaded / event.total) * 100));
            }
          : undefined,
      } as Parameters<typeof apiClient.post>[2],
    );
    return response.data;
  },

  /** An action's reference stills, ord asc, each with a signed thumbnail_url (1h TTL). */
  async listActionStills(loraId: string): Promise<ActionStill[]> {
    const response = await apiClient.get<ActionStill[]>(`/admin/loras/${loraId}/action-stills`);
    return response.data;
  },

  /**
   * Partial update of one still. `_silent`: the card reverts + toasts on its
   * own. To CLEAR framing send `{ framing: null }` — never `''` (400).
   */
  async patchActionStill(loraId: string, stillId: string, patch: PatchActionStillDto): Promise<ActionStill> {
    const response = await apiClient.patch<ActionStill>(
      `/admin/loras/${loraId}/action-stills/${stillId}`,
      patch,
      { _silent: true } as Parameters<typeof apiClient.patch>[2],
    );
    return response.data;
  },

  /**
   * Bulk-apply one status to many stills in a SINGLE request (replaces the old
   * per-still PATCH loop — approving a 100+-still ingest was 100+ round-trips).
   * `_silent`: the caller toasts the {updated}/{skipped} summary itself. Ids
   * owned by a different LoRA come back under `skipped`, never as an error.
   */
  async bulkSetActionStillStatus(
    loraId: string,
    stillIds: string[],
    status: ActionStillStatus,
  ): Promise<BulkStillStatusResult> {
    const response = await apiClient.post<BulkStillStatusResult>(
      `/admin/loras/${loraId}/action-stills/bulk-status`,
      { still_ids: stillIds, status },
      { _silent: true } as Parameters<typeof apiClient.post>[2],
    );
    return response.data;
  },

  /**
   * Synchronous single-still region detection (SP4 B6). `regions` is non-null
   * only on verdict `ok`. `_silent`: the card's mutation toasts a
   * verdict-mapped message itself — the global interceptor would double up.
   */
  async detectStillRegions(
    loraId: string,
    stillId: string,
  ): Promise<{ verdict: RegionsMetaView['verdict']; regions: Record<string, string> | null }> {
    const response = await apiClient.post<{ verdict: RegionsMetaView['verdict']; regions: Record<string, string> | null }>(
      `/admin/loras/${loraId}/action-stills/${stillId}/detect-regions`,
      undefined,
      { _silent: true } as Parameters<typeof apiClient.post>[2],
    );
    return response.data;
  },

  /**
   * FD-3 minimal manual override (founder Gate-1, 2026-07-21): force a still
   * to the no-partner verdict when the detector persistently boxes a phantom
   * partner. Clears any stored regions server-side. `_silent`: the card's
   * mutation toasts its own confirmation — the global interceptor would double up.
   */
  async overrideStillNoPartner(
    loraId: string,
    stillId: string,
  ): Promise<{ verdict: RegionsMetaView['verdict']; regions: Record<string, string> | null }> {
    const response = await apiClient.post<{ verdict: RegionsMetaView['verdict']; regions: Record<string, string> | null }>(
      `/admin/loras/${loraId}/action-stills/${stillId}/regions-override`,
      { mode: 'no_partner' },
      { _silent: true } as Parameters<typeof apiClient.post>[2],
    );
    return response.data;
  },

  /**
   * Queue bulk region detection for a LoRA's non-rejected stills. `onlyMissing`
   * (default true) targets only stills with `regions` still NULL; `false`
   * force-re-detects everything. Returns `{ queued }` immediately — detection
   * runs server-side and the caller's existing stills-query refetch picks up
   * the trickle-in. `_silent`: the caller toasts the queued count / failure.
   */
  async detectLoraRegions(loraId: string, onlyMissing = true): Promise<{ queued: number }> {
    const response = await apiClient.post<{ queued: number }>(
      `/admin/loras/${loraId}/action-stills/detect-regions`,
      { only_missing: onlyMissing },
      { _silent: true } as Parameters<typeof apiClient.post>[2],
    );
    return response.data;
  },

  /**
   * Queue the retrieval-caption sweep for this action. Returns `{ queued }`
   * immediately; captioning trickles in server-side. On an act not cleared for
   * the current caption contract the server narrows this to stills with no
   * caption at all. A second sweep while one is already in flight is deferred
   * (`{ queued: 0, deferred: true }`) and coalesces into a re-run rather than
   * being dropped. `_silent`: the caller toasts the result itself.
   *
   * `scope` is the scope the server actually ran and `narrowed` says whether the
   * server downgraded the one it was asked for. Callers MUST read `narrowed`
   * before reporting `queued: 0` as "nothing to do": on a narrowed act it means
   * every ELIGIBLE still already had text, which is NOT the same claim as "every
   * still on this act has a machine-written description".
   */
  async captionActionStills(loraId: string): Promise<CaptionSweepResult> {
    const response = await apiClient.post<CaptionSweepResult>(
      `/admin/loras/${loraId}/action-stills/caption`,
      {},
      { _silent: true } as Parameters<typeof apiClient.post>[2],
    );
    return response.data;
  },

  /** An action's clauses (kind base|action), ord asc. */
  async listActionClauses(loraId: string): Promise<ImageActionClause[]> {
    const response = await apiClient.get<ImageActionClause[]>(`/admin/loras/${loraId}/action-clauses`);
    return response.data;
  },

  /**
   * Create a base or action clause. `_silent`: the count section renders the
   * server's 400 `reason` inline beneath the textarea (never a toast).
   */
  async createActionClause(
    loraId: string,
    body: { kind: 'base' | 'action'; clause_text: string; character_count?: number },
  ): Promise<ImageActionClause> {
    const response = await apiClient.post<ImageActionClause>(
      `/admin/loras/${loraId}/action-clauses`,
      body,
      { _silent: true } as Parameters<typeof apiClient.post>[2],
    );
    return response.data;
  },

  /** Edit a clause (re-validated server-side against the effective values). `_silent` — inline 400 reason. */
  async updateActionClause(
    loraId: string,
    clauseId: string,
    body: { kind?: 'base' | 'action'; clause_text?: string; character_count?: number; ord?: number },
  ): Promise<ImageActionClause> {
    const response = await apiClient.patch<ImageActionClause>(
      `/admin/loras/${loraId}/action-clauses/${clauseId}`,
      body,
      { _silent: true } as Parameters<typeof apiClient.patch>[2],
    );
    return response.data;
  },

  async deleteActionClause(loraId: string, clauseId: string): Promise<{ success: true }> {
    const response = await apiClient.delete<{ success: true }>(
      `/admin/loras/${loraId}/action-clauses/${clauseId}`,
    );
    return response.data;
  },

  /** Build summary + per-count support derivation. `_silent`: the header renders its own state. */
  async getActionSummary(loraId: string): Promise<ActionSummary> {
    const response = await apiClient.get<ActionSummary>(
      `/admin/loras/${loraId}/action-summary`,
      { _silent: true } as Parameters<typeof apiClient.get>[1],
    );
    return response.data;
  },

  /** Take the action live. 400s unless ≥1 supported count. `_silent` — the gate already prevents the click. */
  async setActionLive(loraId: string): Promise<{ action_live_at: string }> {
    const response = await apiClient.post<{ action_live_at: string }>(
      `/admin/loras/${loraId}/action-live`,
      undefined,
      { _silent: true } as Parameters<typeof apiClient.post>[2],
    );
    return response.data;
  },

  async clearActionLive(loraId: string): Promise<{ action_live_at: null }> {
    const response = await apiClient.delete<{ action_live_at: null }>(`/admin/loras/${loraId}/action-live`);
    return response.data;
  },

  /** Retire the LoRA-as-style (`retired_at`). Independent of live status. */
  async setActionRetired(loraId: string): Promise<{ retired_at: string }> {
    const response = await apiClient.post<{ retired_at: string }>(`/admin/loras/${loraId}/retire`, undefined);
    return response.data;
  },

  async clearActionRetired(loraId: string): Promise<{ retired_at: null }> {
    const response = await apiClient.delete<{ retired_at: null }>(`/admin/loras/${loraId}/retire`);
    return response.data;
  },

  /**
   * Field-level change history for an image LoRA (`lora_audit` rows via
   * GET /admin/loras/:id/audit), newest first. Same flat field/old/new shape
   * as getVideoLoraAudit — reuses AdminVideoLoraAuditRow.
   */
  async getImageLoraAudit(loraId: string): Promise<AdminVideoLoraAuditRow[]> {
    const response = await apiClient.get<AdminVideoLoraAuditRow[]>(`/admin/loras/${loraId}/audit`);
    return response.data;
  },
};
